import { cloneDeep, flatten, get } from "lodash";
import moment from "moment";
import {
  distributionFormFields,
  DISTRIBUTION_WITHDRAWAL_TYPE,
  manageDistributionFormNames,
  OPTIONS_DATA_MAPPER,
  toOptionValuesFromMapper,
} from "../../../utils";

const formName = manageDistributionFormNames.WITHDRAWAL_REQUEST;
const fields = distributionFormFields[formName];

export const calculateAgeFromDOB = (dob) =>
  Math.floor(moment(new Date()).diff(moment(dob), "years", true));

export const DISTRIBUTION_WITHDRAWAL_MODE = {
  self: 1,
  traditional_IRA: 2,
  roth_IRA: 3,
  employer_Sponsored: 4,
  beneficiary: 5,
};
export const PaymentOptions = {
  EFT: 2,
  MailCheckToAddress: 1,
};
export const TypeOfExpectancyEnum = {
  Single: 0,
  Multiple: 1,
  Others: 2,
};
export const TypeOfExpectancy = {
  0: "My life expectency",
  1: "Combined life expectency",
  2: "Other",
};
export const TypeOfExpectancyForAnnuitization = {
  0: "My life expectency",
  1: "Combined life expectency",
};
export const FrequencyOptions = {
  1: "Annually",
  2: "Semi-Annually",
  3: "Quarterly",
  4: "Monthly",
};
export const DateOfAnnuityPayments = {
  1: "Fixed date",
  2: "Custom date",
};
export const DateOfAnnuityPaymentsEnum = {
  FixedDate: 1,
  PartipicantSelected: 2,
};

export const WithdrawalStrategy = {
  AllowMeToChoose: 1,
  SourceProrationInvestmentProration: 2,
  SourceHierarchyInvestmentProration: 3,
  SourceProrationInvestmentHierarchy: 4,
  SourceHierarchyInvestmentHierarchy: 5,
};
export const SourceAllocationCalculationMethod = {
  Prorata: 1,
  AllowMeToChoose: 2,
  Hierarchy: 3,
};
export const SourceAllocationCalculationMethodOptions = {
  1: "Pro-Rata",
  2: "Allow me to choose",
  3: "Hierarchy",
};

export const WithdrawalAmountType = {
  NetRequestedAmount: 1,
  ChequeFee: 2,
  EFTFee: 3,
  WithdrawalFee: 4,
  RollOverFee: 5,
  StateTax: 6,
  FederalTax: 7,
  Penalty: 8,
};

export const distributionModes = OPTIONS_DATA_MAPPER.DISTRIBUTION_MODES;
export const codeOptionsRollOver = OPTIONS_DATA_MAPPER.CODE_OPTIONS_ROLLOVER;
export const codeOptionsCash = OPTIONS_DATA_MAPPER.CODE_OPTIONS_CASH;
export const distributionTypeEnum = DISTRIBUTION_WITHDRAWAL_TYPE;
export const PERIODIC_PAYMENT_OPTIONS =
  OPTIONS_DATA_MAPPER.PERIODIC_PAYMENT_OPTIONS;
export const getFullName = (employee) =>
  `${employee.firstName} ${employee.middleName} ${employee.lastName}`;
export const getAddressDisplay = (address) => `${address?.address1}`;
export const getBankDisplay = (bank) => `${bank?.checkingSavingsAccountNumber}`;
export const getCodeOptions = (transaction_) =>
  OPTIONS_DATA_MAPPER.CODE_OPTIONS_CASH_ROLLOVER;

export const AllowMeToChooseFromOptions = {
  1: "Source",
  2: "Investments",
};

export const CustomizeAllocationCalculationMethodEnum = {
  Source: 1,
  Investment: 2,
};

export const isFederalTaxApplicable = (withdrawalRequest) =>
  ![DISTRIBUTION_WITHDRAWAL_TYPE.QBAD].includes(
    withdrawalRequest[fields.withdrawalType]
  );
export const isQBAD = (withdrawalRequest) =>
  withdrawalRequest[fields.withdrawalType] ===
  DISTRIBUTION_WITHDRAWAL_TYPE.QBAD;
export const isOneTimePayment = (withdrawalRequestData) =>
  withdrawalRequestData[fields.modeOfPayment] === 1;
export const isPeriodicPayment = (withdrawalRequestData) =>
  withdrawalRequestData[fields.modeOfPayment] === 2;
export const isFullWithdrawal = (withdrawalRequestData) =>
  withdrawalRequestData[fields.withdrawEntireAmount] === true;
export const isPartialWithdrawal = (withdrawalRequestData) =>
  withdrawalRequestData[fields.withdrawEntireAmount] === false;
export const isRolloverDistributionMode = (distributionMode) =>
  [
    DISTRIBUTION_WITHDRAWAL_MODE.employer_Sponsored,
    DISTRIBUTION_WITHDRAWAL_MODE.traditional_IRA,
    DISTRIBUTION_WITHDRAWAL_MODE.roth_IRA,
  ].includes(distributionMode);

export const periodicPaymentEnum =
  OPTIONS_DATA_MAPPER.PERIODIC_PAYMENT_OPTIONS_ENUM;
export const isAnnutizationOrSpecificTenure = (withdrawalRequestData) =>
  isPeriodicPayment(withdrawalRequestData) &&
  [
    periodicPaymentEnum.AnnuitizationMethod,
    periodicPaymentEnum.SpecificTenure,
  ].includes(withdrawalRequestData[fields.periodicPaymentType]);

export const isSpecificTenure = (withdrawalRequestData) =>
  isPeriodicPayment(withdrawalRequestData) &&
  [periodicPaymentEnum.SpecificTenure].includes(
    withdrawalRequestData[fields.periodicPaymentType]
  );
export const isSpecificDollar = (withdrawalRequestData) =>
  isPeriodicPayment(withdrawalRequestData) &&
  [periodicPaymentEnum.SpecificDollar].includes(
    withdrawalRequestData[fields.periodicPaymentType]
  );

export const SourceSubCategory = {
  EmployerQMAC: 1,
  EmployerQNEC: 2,
  EmployeePreTax: 4,
  EmployeeRoth: 5,
  EmployeeAfterTax: 6,
  EmployeePreTaxRollover: 7,
  EmployeeRothRollover: 8,
  EmployeeAfterTaxRollover: 9,
  EmployeeCatchUp: 10,
};

export const withdrawalTypeEnum = {
  Inservice: 1,
  Hardship: 2,
  RequiredMinimumDistribution: 3,
  SeparationFromService: 4,
  Permissible: 5,
  ForceOut: 6,
  QBAD: 7,
};

export const RestrictedTypes = {
  Allocation: 1,
  ReAllocation: 2,
  Rebalancing: 4,
  TransferIn: 8,
  TransferOut: 16,
  Insider: 32,
  RolloverIn: 64,
  LoansHardship: 128,
  InserviceWithdrawal: 256,
  HardshipWithdrawal: 512,
  SFSWithdrawal: 1024,
  RMD: 2048,
  Forceout: 4096,
  PermissibleWithdrawal: 8192,
};

// export const GetRestrictedType = (type) => {
//   switch (type) {
//     case 1:
//       return 256;
//     case 2:
//       return 512;
//     case 3:
//       return 2048;
//     case 4:
//       return 1024;
//     case 5:
//       return 8192;
//     case 6:
//       return 4096;
//     default:
//       return 256;
//   }
// };

export const GetRestrictedType = (withDrawalType) => {
  switch (withDrawalType) {
    case withdrawalTypeEnum.Inservice:
      return RestrictedTypes.InserviceWithdrawal;
    case withdrawalTypeEnum.Hardship:
      return RestrictedTypes.HardshipWithdrawal;
    case withdrawalTypeEnum.SeparationFromService:
      return RestrictedTypes.SFSWithdrawal;
    case withdrawalTypeEnum.RequiredMinimumDistribution:
      return RestrictedTypes.RMD;
    case withdrawalTypeEnum.Permissible:
      return RestrictedTypes.PermissibleWithdrawal;
    case withdrawalTypeEnum.ForceOut:
      return RestrictedTypes.Forceout;
    default:
      return 0;
  }
};
export const getPeriodicPaymentType = (values) =>
  values[fields.periodicPaymentType] ===
  OPTIONS_DATA_MAPPER.PERIODIC_PAYMENT_OPTIONS_ENUM.SpecificTenure
    ? "SpecificTenure"
    : values[fields.periodicPaymentType] ===
      OPTIONS_DATA_MAPPER.PERIODIC_PAYMENT_OPTIONS_ENUM.AnnuitizationMethod
    ? "Annuitization"
    : "";

export const calculationToBeBasedOnGrossAmount = (withdrawalRequestData) => {
  return (
    isFullWithdrawal(withdrawalRequestData) &&
    (isOneTimePayment(withdrawalRequestData) ||
      (isPeriodicPayment(withdrawalRequestData) &&
        isAnnutizationOrSpecificTenure(withdrawalRequestData)))
  );
};

export const getVestedBalanceFromSourceEmployeeMap = (
  sourceEmployeeMap,
  vestingMultiplier
) =>
  getValues(vestingMultiplier) *
    getValues(sourceEmployeeMap?.regularAccountBalance) +
  getValues(sourceEmployeeMap?.basisAccountBalance) +
  getValues(sourceEmployeeMap?.repaymentAccountBalance);

export const calculateTotalVestedBalanceFromSourceBalance = (sourceBalance) =>
  sourceBalance?.reduce(
    (a, v) =>
      (a =
        a +
        getVestedBalanceFromSourceEmployeeMap(
          v.sourceEmployeeMap,
          v.vestingMultiplier
        )),
    0
  );

export const getVestedBalanceWithoutVestingMultiplier = (sourceBalance) =>
  sourceBalance?.reduce(
    (a, v) => (a = a + getValues(v.sourceEmployeeMap?.accountBalance)),
    0
  );

export const isAllowMeToChooseTransaction = (transaction) =>
  transaction[fields.sourceAllocationCalculationMethod] ===
  SourceAllocationCalculationMethod.AllowMeToChoose;

export const shouldIncludePenaltyBasedOnYOS = (
  employeePlanYOS,
  selectedWithdrawalPlanSetUp,
  withdrawalRequest
) =>
  employeePlanYOS < selectedWithdrawalPlanSetUp.planYOS &&
  (withdrawalRequest.ageInYears < selectedWithdrawalPlanSetUp.years ||
    (withdrawalRequest.ageInYears <= selectedWithdrawalPlanSetUp.years &&
      withdrawalRequest.ageInMonths < selectedWithdrawalPlanSetUp.months));

export function CalculateInvestmentProrationSplitup(
  source,
  newSourceMap,
  sourceBalance
) {
  let investmentList = [];
  get(source, "sourceInvestmentBalances", []).forEach((investment) => {
    let newInvestment = { ...investment };
    let newInvestmentMap = {};
    let investmentBalance = investment?.sourceEmployeeInvestmentMap;

    // newInvestmentMap.accountBalance = parseFloat(
    //   (investmentBalance?.accountBalance / sourceBalance?.accountBalance) *
    //   newSourceMap?.accountBalance
    // );
    // newInvestmentMap.regularAccountBalance =
    //   (investmentBalance.regularAccountBalance /
    //     investmentBalance.accountBalance) *
    //   newInvestmentMap.accountBalance;
    // newInvestmentMap.repaymentAccountBalance =
    //   (investmentBalance.repaymentAccountBalance /
    //     investmentBalance.accountBalance) *
    //   newInvestmentMap.accountBalance;
    // newInvestmentMap.basisAccountBalance =
    //   (investmentBalance.basisAccountBalance /
    //     investmentBalance.accountBalance) *
    //   newInvestmentMap.accountBalance;
    newInvestmentMap.accountBalance =
      sourceBalance?.accountBalance == 0
        ? 0
        : parseFloat(
            (investmentBalance?.accountBalance /
              sourceBalance?.accountBalance) *
              newSourceMap?.accountBalance
          );
    newInvestmentMap.investmentWithdrawalAmount =
      newInvestmentMap.accountBalance;
    newInvestmentMap.regularAccountBalance =
      investmentBalance?.accountBalance == 0
        ? 0
        : (investmentBalance?.regularAccountBalance /
            investmentBalance?.accountBalance) *
          newInvestmentMap.accountBalance;
    newInvestmentMap.repaymentAccountBalance =
      investmentBalance?.accountBalance == 0
        ? 0
        : (investmentBalance?.repaymentAccountBalance /
            investmentBalance?.accountBalance) *
          newInvestmentMap.accountBalance;
    newInvestmentMap.basisAccountBalance =
      investmentBalance?.accountBalance == 0
        ? 0
        : (investmentBalance?.basisAccountBalance /
            investmentBalance?.accountBalance) *
          newInvestmentMap.accountBalance;

    newInvestmentMap.regularCCBBalance =
      investmentBalance?.regularAccountBalance == 0
        ? 0
        : (investmentBalance?.regularCCBBalance /
            investmentBalance?.regularAccountBalance) *
          newInvestmentMap.regularAccountBalance;
    newInvestmentMap.regularEarningsBalance =
      investmentBalance?.regularAccountBalance == 0
        ? 0
        : (investmentBalance?.regularEarningsBalance /
            investmentBalance?.regularAccountBalance) *
          newInvestmentMap.regularAccountBalance;
    newInvestmentMap.repaymentCCBBalance =
      investmentBalance?.repaymentAccountBalance == 0
        ? 0
        : (investmentBalance?.repaymentCCBBalance /
            investmentBalance?.repaymentAccountBalance) *
          newInvestmentMap.repaymentAccountBalance;
    newInvestmentMap.repaymentEarningsBalance =
      investmentBalance?.repaymentAccountBalance == 0
        ? 0
        : (investmentBalance?.repaymentEarningsBalance /
            investmentBalance?.repaymentAccountBalance) *
          newInvestmentMap.repaymentAccountBalance;
    newInvestmentMap.basisCCBBalance =
      investmentBalance?.basisAccountBalance == 0
        ? 0
        : (investmentBalance?.basisCCBBalance /
            investmentBalance?.basisAccountBalance) *
          newInvestmentMap.basisAccountBalance;
    newInvestmentMap.basisEarningsBalance =
      investmentBalance?.basisAccountBalance == 0
        ? 0
        : (investmentBalance?.basisEarningsBalance /
            investmentBalance?.basisAccountBalance) *
          newInvestmentMap.basisAccountBalance;

    newInvestment.sourceEmployeeInvestmentMap = newInvestmentMap;
    investmentList.push(newInvestment);
  });
  return investmentList;
}

export function CalculateProrationSplitUp(
  tempEmployeeSourceBalances,
  amount,
  tempRequestedAmount,
  isAllowMeToChoose
) {
  const employeeSourceBalances = cloneDeep(tempEmployeeSourceBalances);
  const requestedAmount = cloneDeep(tempRequestedAmount);
  let totalVestedAccountBalance = getVestedBalanceWithoutVestingMultiplier(
    employeeSourceBalances
  );
  totalVestedAccountBalance = getValues(totalVestedAccountBalance);

  let sourceList = [];
  employeeSourceBalances.map((source) => {
    let newSource = { ...source };
    let sourceBalance = source.sourceEmployeeMap;
    let sourceVestingMultiplier = 1;
    let newSourceMap = {};
    newSourceMap.accountBalance = isAllowMeToChoose
      ? getValues(
          requestedAmount.find((_) => _.sourceId == source.sourceId)
            ?.amountRequested
        )
      : totalVestedAccountBalance == 0
      ? 0
      : parseFloat(
          (getValues(sourceBalance?.accountBalance) /
            totalVestedAccountBalance) *
            amount
        );
    newSourceMap.sourceWithdrawalAmount = newSourceMap.accountBalance;
    newSourceMap.regularAccountBalance =
      (sourceBalance.regularAccountBalance / sourceBalance.accountBalance) *
      newSourceMap.accountBalance;
    newSourceMap.repaymentAccountBalance =
      (sourceBalance.repaymentAccountBalance / sourceBalance.accountBalance) *
      newSourceMap.accountBalance;
    newSourceMap.basisAccountBalance =
      (sourceBalance.basisAccountBalance / sourceBalance.accountBalance) *
      newSourceMap.accountBalance;
    newSourceMap.regularCCBBalance =
      sourceBalance.regularAccountBalance == 0
        ? 0
        : (sourceBalance.regularCCBBalance /
            sourceBalance.regularAccountBalance) *
          newSourceMap.regularAccountBalance;
    newSourceMap.regularEarningsBalance =
      sourceBalance.regularAccountBalance == 0
        ? 0
        : (sourceBalance.regularEarningsBalance /
            sourceBalance.regularAccountBalance) *
          newSourceMap.regularAccountBalance;
    newSourceMap.repaymentCCBBalance =
      sourceBalance.repaymentAccountBalance == 0
        ? 0
        : (sourceBalance.repaymentCCBBalance /
            sourceBalance.repaymentAccountBalance) *
          newSourceMap.repaymentAccountBalance;
    newSourceMap.repaymentEarningsBalance =
      sourceBalance.repaymentAccountBalance == 0
        ? 0
        : (sourceBalance.repaymentEarningsBalance /
            sourceBalance.repaymentAccountBalance) *
          newSourceMap.repaymentAccountBalance;
    newSourceMap.basisCCBBalance =
      sourceBalance.basisAccountBalance == 0
        ? 0
        : (sourceBalance.basisCCBBalance / sourceBalance.basisAccountBalance) *
          newSourceMap.basisAccountBalance;
    newSourceMap.basisEarningsBalance =
      sourceBalance.basisAccountBalance == 0
        ? 0
        : (sourceBalance.basisEarningsBalance /
            sourceBalance.basisAccountBalance) *
          newSourceMap.basisAccountBalance;

    let investmentList = CalculateInvestmentProrationSplitup(
      { ...source },
      newSourceMap,
      sourceBalance
    );
    newSourceMap.sourceEmployeeInvestmentMaps = investmentList;
    newSource.sourceInvestmentBalances = investmentList;

    // }
    newSource.sourceEmployeeMap = newSourceMap;
    sourceList.push(newSource);
  });
  return sourceList;
}

export const sourceBalanceToSourceAllocationMap = (sourceBalance) => {
  return {
    ...sourceBalance,
    sourceId: sourceBalance.sourceId,
    sourceName: sourceBalance.sourceName,
    sourceWithdrawalAmount:
      sourceBalance.sourceEmployeeMap?.sourceWithdrawalAmount ??
      sourceBalance.sourceEmployeeMap?.accountBalance,
    allocatedInvestmentWithdrawals: get(
      sourceBalance,
      "sourceEmployeeMap.sourceEmployeeInvestmentMaps",
      []
    ).map((investmentBalance) => {
      return {
        ...investmentBalance,
        sourceWithdrawalId: 0,
        investmentId: investmentBalance.investmentId,
        investmentName: investmentBalance.investmentName,
        investmentWithdrawalAmount:
          investmentBalance.sourceEmployeeInvestmentMap
            .investmentWithdrawalAmount ??
          investmentBalance.sourceEmployeeInvestmentMap.accountBalance,
      };
    }),
  };
};

export function CalculateProrationForSourceAllocation( ////data structure change
  tempEmployeeSourceBalances,
  amount,
  withdrawEntireAmount
) {
  const employeeSourceBalances = cloneDeep(tempEmployeeSourceBalances);
  if (withdrawEntireAmount) {
    const totalVestedAccountBalance = getVestedBalanceWithoutVestingMultiplier(
      employeeSourceBalances
    );
    const proratedSourceBalances = CalculateProrationSplitUp(
      employeeSourceBalances,
      totalVestedAccountBalance,
      [],
      false
    );
    return proratedSourceBalances;
  } else {
    const proratedSourceBalances = CalculateProrationSplitUp(
      employeeSourceBalances,
      amount,
      [],
      false
    );
    return proratedSourceBalances;
  }
}

export function SourceBalancesAfterVestingMultiplier(
  tempEmployeeSourceBalances
) {
  const sourceOrInvestmentAfterVestingMultiplier = (
    sourceOrInvestment,
    sourceVestingMultiplier
  ) => {
    const tempSourceOrInvestment = {
      ...sourceOrInvestment,
      regularAccountBalance:
        sourceOrInvestment.regularAccountBalance * sourceVestingMultiplier,
      repaymentAccountBalance: sourceOrInvestment.repaymentAccountBalance,
      basisAccountBalance: sourceOrInvestment.basisAccountBalance,
      regularCCBBalance:
        sourceOrInvestment.regularCCBBalance * sourceVestingMultiplier,
      regularEarningsBalance:
        sourceOrInvestment.regularEarningsBalance * sourceVestingMultiplier,
      repaymentCCBBalance: sourceOrInvestment.repaymentCCBBalance,
      repaymentEarningsBalance: sourceOrInvestment.repaymentEarningsBalance,
      basisCCBBalance: sourceOrInvestment.basisCCBBalance,
      basisEarningsBalance: sourceOrInvestment.basisEarningsBalance,
      regularBalanceUnits:
        sourceOrInvestment.regularBalanceUnits * sourceVestingMultiplier,
    };
    return {
      ...tempSourceOrInvestment,
      accountBalance:
        tempSourceOrInvestment.regularAccountBalance +
        tempSourceOrInvestment.repaymentAccountBalance +
        tempSourceOrInvestment.basisAccountBalance,
    };
  };

  const employeeSourceBalances = cloneDeep(tempEmployeeSourceBalances);
  const sourceBalancesAfterVestingMultiplier = employeeSourceBalances.map(
    (source) => {
      let newSource = { ...source };
      let sourceBalance = source.sourceEmployeeMap;
      let sourceVestingMultiplier = source.vestingMultiplier;
      let newSourceMap = {};
      newSourceMap = sourceOrInvestmentAfterVestingMultiplier(
        sourceBalance,
        sourceVestingMultiplier
      );

      let investmentList = get(source, "sourceInvestmentBalances", []).map(
        (investment) => {
          let newInvestment = { ...investment };
          let newInvestmentMap = {};
          newInvestmentMap = sourceOrInvestmentAfterVestingMultiplier(
            investment.sourceEmployeeInvestmentMap,
            sourceVestingMultiplier
          );
          let investmentBalance = investment?.sourceEmployeeInvestmentMap;
          newInvestment.sourceEmployeeInvestmentMap = newInvestmentMap;
          return newInvestment;
        }
      );
      newSourceMap.sourceEmployeeInvestmentMaps = investmentList;
      newSource.sourceInvestmentBalances = investmentList;

      newSource.sourceEmployeeMap = newSourceMap;
      return newSource;
    }
  );
  return sourceBalancesAfterVestingMultiplier;
}

//////// Hierarchy

export function GetWithdrawalAmountBasedOnStrategy(
  withdrawalStrategy,
  tempEmployeeSourceBalances,
  hierarchy,
  requestedAmount,
  sourceWiseRequestAmountTemp
) {
  const employeeSourceBalances = cloneDeep(tempEmployeeSourceBalances);
  const sourceWiseRequestAmount = cloneDeep(sourceWiseRequestAmountTemp);
  switch (withdrawalStrategy) {
    case WithdrawalStrategy.SourceProrationInvestmentProration:
      return CalculateProrationSplitUp(
        employeeSourceBalances,
        requestedAmount,
        sourceWiseRequestAmount,
        false
      );
    case WithdrawalStrategy.AllowMeToChoose:
      return CalculateProrationSplitUp(
        employeeSourceBalances,
        0,
        sourceWiseRequestAmount,
        true
      );
    case WithdrawalStrategy.SourceHierarchyInvestmentProration:
      return CalculateSourceHierarchySplit(
        employeeSourceBalances,
        hierarchy,
        requestedAmount
      );
    case WithdrawalStrategy.SourceProrationInvestmentHierarchy:
      return CalculateInvestmentHierarchySplit(
        employeeSourceBalances,
        hierarchy,
        requestedAmount
      );
    case WithdrawalStrategy.SourceHierarchyInvestmentHierarchy:
      return CalculateSourceHierarchyInvestmentHierarchySplit(
        employeeSourceBalances,
        hierarchy,
        requestedAmount
      );
    default:
      return [];
  }
}

export function CalculateSourceHierarchySplit(
  tempEmployeeSourceBalances,
  hierarchy_,
  requestedAmount
) {
  const employeeSourceBalances = cloneDeep(tempEmployeeSourceBalances);
  let sourceList = [];
  let hierarchy = cloneDeep(hierarchy_);
  let maxHierarchy = hierarchy?.sort(
    (a, b) => a.hierarchyNumber - b.hierarchyNumber
  )[0]?.hierarchyNumber;
  let accountBalance = requestedAmount;
  for (let i = 1; i <= maxHierarchy; i++) {
    let sourceWithdrawalAmount = 0;
    let hierarchies = hierarchy?.filter((_) => _.hierarchyNumber == i);
    let matchSources = employeeSourceBalances.filter((_) =>
      hierarchies.map((x) => x.sourceId).includes(_.sourceId)
    );
    let totalSourceAmount =
      matchSources?.reduce(
        (a, b) => a + b.sourceEmployeeMap.accountBalance,
        0
      ) ?? 0;
    if (matchSources == null) continue;
    let remainingAmount = accountBalance;
    for (let n_ in matchSources) {
      let n = matchSources[n_];
      let source = { ...n };
      let newSourceEmployeeMap = {};
      let sourceBalance = n.sourceEmployeeMap;
      if (totalSourceAmount <= accountBalance)
        sourceWithdrawalAmount = n.sourceEmployeeMap.accountBalance;
      else
        sourceWithdrawalAmount =
          (n.sourceEmployeeMap.accountBalance / totalSourceAmount) *
          accountBalance;

      remainingAmount -= sourceWithdrawalAmount;

      newSourceEmployeeMap.accountBalance = sourceWithdrawalAmount;
      newSourceEmployeeMap.regularAccountBalance =
        (sourceBalance.regularAccountBalance / sourceBalance.accountBalance) *
        newSourceEmployeeMap.accountBalance;
      newSourceEmployeeMap.repaymentAccountBalance =
        (sourceBalance.repaymentAccountBalance / sourceBalance.accountBalance) *
        newSourceEmployeeMap.accountBalance;
      newSourceEmployeeMap.basisAccountBalance =
        (sourceBalance.basisAccountBalance / sourceBalance.accountBalance) *
        newSourceEmployeeMap.accountBalance;
      newSourceEmployeeMap.regularCCBBalance =
        sourceBalance.regularAccountBalance == 0
          ? 0
          : (sourceBalance.regularCCBBalance /
              sourceBalance.regularAccountBalance) *
            newSourceEmployeeMap.regularAccountBalance;
      newSourceEmployeeMap.regularEarningsBalance =
        sourceBalance.regularAccountBalance == 0
          ? 0
          : (sourceBalance.regularEarningsBalance /
              sourceBalance.regularAccountBalance) *
            newSourceEmployeeMap.regularAccountBalance;
      newSourceEmployeeMap.repaymentCCBBalance =
        sourceBalance.repaymentAccountBalance == 0
          ? 0
          : (sourceBalance.repaymentCCBBalance /
              sourceBalance.repaymentAccountBalance) *
            newSourceEmployeeMap.repaymentAccountBalance;
      newSourceEmployeeMap.repaymentEarningsBalance =
        sourceBalance.repaymentAccountBalance == 0
          ? 0
          : (sourceBalance.repaymentEarningsBalance /
              sourceBalance.repaymentAccountBalance) *
            newSourceEmployeeMap.repaymentAccountBalance;
      newSourceEmployeeMap.basisCCBBalance =
        sourceBalance.basisAccountBalance == 0
          ? 0
          : (sourceBalance.basisCCBBalance /
              sourceBalance.basisAccountBalance) *
            newSourceEmployeeMap.basisAccountBalance;
      newSourceEmployeeMap.basisEarningsBalance =
        sourceBalance.basisAccountBalance == 0
          ? 0
          : (sourceBalance.basisEarningsBalance /
              sourceBalance.basisAccountBalance) *
            newSourceEmployeeMap.basisAccountBalance;

      let investmentList = [];
      n.sourceInvestmentBalances.forEach((investment) => {
        let investmentBalance = { ...investment?.sourceEmployeeInvestmentMap };
        let newInvestmentMap = {};
        newInvestmentMap.accountBalance = parseFloat(
          (investmentBalance.accountBalance / sourceBalance.accountBalance) *
            sourceWithdrawalAmount
        );
        newInvestmentMap.regularAccountBalance =
          (investmentBalance.regularAccountBalance /
            investmentBalance.accountBalance) *
          newInvestmentMap.accountBalance;
        newInvestmentMap.repaymentAccountBalance =
          (investmentBalance.repaymentAccountBalance /
            investmentBalance.accountBalance) *
          newInvestmentMap.accountBalance;
        newInvestmentMap.basisAccountBalance =
          (investmentBalance.basisAccountBalance /
            investmentBalance.accountBalance) *
          newInvestmentMap.accountBalance;
        newInvestmentMap.regularCCBBalance =
          investmentBalance.regularAccountBalance == 0
            ? 0
            : (investmentBalance.regularCCBBalance /
                investmentBalance.regularAccountBalance) *
              newInvestmentMap.regularAccountBalance;
        newInvestmentMap.regularEarningsBalance =
          investmentBalance.regularAccountBalance == 0
            ? 0
            : (investmentBalance.regularEarningsBalance /
                investmentBalance.regularAccountBalance) *
              newInvestmentMap.regularAccountBalance;
        newInvestmentMap.repaymentCCBBalance =
          investmentBalance.repaymentAccountBalance == 0
            ? 0
            : (investmentBalance.repaymentCCBBalance /
                investmentBalance.repaymentAccountBalance) *
              newInvestmentMap.repaymentAccountBalance;
        newInvestmentMap.repaymentEarningsBalance =
          investmentBalance.repaymentAccountBalance == 0
            ? 0
            : (investmentBalance.repaymentEarningsBalance /
                investmentBalance.repaymentAccountBalance) *
              newInvestmentMap.repaymentAccountBalance;
        newInvestmentMap.basisCCBBalance =
          investmentBalance.basisAccountBalance == 0
            ? 0
            : (investmentBalance.basisCCBBalance /
                investmentBalance.basisAccountBalance) *
              newInvestmentMap.basisAccountBalance;
        newInvestmentMap.basisEarningsBalance =
          investmentBalance.basisAccountBalance == 0
            ? 0
            : (investmentBalance.basisEarningsBalance /
                investmentBalance.basisAccountBalance) *
              newInvestmentMap.basisAccountBalance;

        let newInvestment = { ...investment };
        newInvestment.sourceEmployeeInvestmentMap = newInvestmentMap;
        investmentList.push(newInvestment);
      });
      source.sourceEmployeeMap = {
        ...newSourceEmployeeMap,
        sourceEmployeeInvestmentMaps: investmentList,
      };
      source.sourceInvestmentBalances = investmentList;
      sourceList.push(source);
    }
    accountBalance = remainingAmount;
  }
  return sourceList;
}
// export function InvestmentHierarchySplit(hierachies_, source, sourceAmount) {
//   let investmentList = [];
//   let hierachies = cloneDeep(hierachies_);
//   let investmentSource = hierachies?.find((_) => _.sourceId == source.sourceId);
//   let maxHierarchy = investmentSource?.investmentHierarchies?.sort(
//     (a, b) => b.hierarchyNumber - a.hierarchyNumber
//   )[0]?.hierarchyNumber;
//   for (let i = 1; i <= maxHierarchy; i++) {
//     //////undo this
//     let investmentWithdrawalAmount;
//     let investmentSources = investmentSource?.investmentHierarchies.filter(
//       (_) => _.hierarchyNumber == i
//     );
//     let combinedInvestments = source.sourceInvestmentBalances.filter((_) =>
//       investmentSources.map((x) => x.investmentId).includes(_.investmentId)
//     );
//     if (combinedInvestments != null && combinedInvestments.length > 0) {
//       let investmentAmount = combinedInvestments?.reduce(
//         (a, b) => a + b.sourceEmployeeInvestmentMap.accountBalance,
//         0
//       );
//       let remainingAmount = sourceAmount;
//       for (
//         let investmentIndex = 0;
//         investmentIndex < combinedInvestments.length;
//         investmentIndex++
//       ) {
//         let investment = { ...combinedInvestments[investmentIndex] };
//         let investmentBalance = investment.sourceEmployeeInvestmentMap;
//         let multiplier =
//           (investmentBalance.accountBalance / investmentAmount) * 100;
//         if (investmentAmount <= sourceAmount)
//           investmentWithdrawalAmount = (multiplier * sourceAmount) / 100;
//         else investmentWithdrawalAmount = (multiplier * sourceAmount) / 100;

//         remainingAmount -= investmentWithdrawalAmount;

//         let newInvestmentMap = {};
//         newInvestmentMap.accountBalance = investmentWithdrawalAmount;
//         newInvestmentMap.regularAccountBalance =
//           (investmentBalance.regularAccountBalance /
//             investmentBalance.accountBalance) *
//           investmentWithdrawalAmount;
//         newInvestmentMap.repaymentAccountBalance =
//           (investmentBalance.repaymentAccountBalance /
//             investmentBalance.accountBalance) *
//           investmentWithdrawalAmount;
//         newInvestmentMap.basisAccountBalance =
//           (investmentBalance.basisAccountBalance /
//             investmentBalance.accountBalance) *
//           investmentWithdrawalAmount;
//         newInvestmentMap.regularCCBBalance =
//           investmentBalance.regularAccountBalance == 0
//             ? 0
//             : (investmentBalance.regularCCBBalance /
//                 investmentBalance.regularAccountBalance) *
//               newInvestmentMap.regularAccountBalance;
//         newInvestmentMap.regularEarningsBalance =
//           investmentBalance.regularAccountBalance == 0
//             ? 0
//             : (investmentBalance.regularEarningsBalance /
//                 investmentBalance.regularAccountBalance) *
//               newInvestmentMap.regularAccountBalance;
//         newInvestmentMap.repaymentCCBBalance =
//           investmentBalance.repaymentAccountBalance == 0
//             ? 0
//             : (investmentBalance.repaymentCCBBalance /
//                 investmentBalance.repaymentAccountBalance) *
//               newInvestmentMap.repaymentAccountBalance;
//         newInvestmentMap.repaymentEarningsBalance =
//           investmentBalance.repaymentAccountBalance == 0
//             ? 0
//             : (investmentBalance.repaymentEarningsBalance /
//                 investmentBalance.repaymentAccountBalance) *
//               newInvestmentMap.repaymentAccountBalance;
//         newInvestmentMap.basisCCBBalance =
//           investmentBalance.basisAccountBalance == 0
//             ? 0
//             : (investmentBalance.basisCCBBalance /
//                 investmentBalance.basisAccountBalance) *
//               newInvestmentMap.basisAccountBalance;
//         newInvestmentMap.basisEarningsBalance =
//           investmentBalance.basisAccountBalance == 0
//             ? 0
//             : (investmentBalance.basisEarningsBalance /
//                 investmentBalance.basisAccountBalance) *
//               newInvestmentMap.basisAccountBalance;

//         let newInvestment = { ...investment };
//         newInvestment.sourceEmployeeInvestmentMap = newInvestmentMap;
//         investmentList.push(newInvestment);
//       }
//       sourceAmount = remainingAmount;
//     }
//   }
//   return investmentList;
// }

export function InvestmentHierarchySplit(hierachies_, source, sourceAmount) {
  let investmentList = [];
  let hierachies = cloneDeep(hierachies_);
  let investmentSource = hierachies?.find((_) => _.sourceId == source.sourceId);
  let maxHierarchy = investmentSource?.investmentHierarchies?.sort(
    (a, b) => b.hierarchyNumber - a.hierarchyNumber
  )[0]?.hierarchyNumber;
  for (let i = 1; i <= maxHierarchy; i++) {
    //////undo this
    let investmentWithdrawalAmountRegular;
    let investmentWithdrawalAmountRepayment;
    let investmentWithdrawalAmountBasis;
    let investmentSources = investmentSource?.investmentHierarchies.filter(
      (_) => _.hierarchyNumber == i
    );
    let combinedInvestments = source.sourceInvestmentBalances.filter((_) =>
      investmentSources.map((x) => x.investmentId).includes(_.investmentId)
    );
    if (combinedInvestments != null && combinedInvestments.length > 0) {
      let investmentAmount = combinedInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.accountBalance,
        0
      );
      let investmentAmountRegular = combinedInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.regularAccountBalance,
        0
      );
      let investmentAmountRepayment = combinedInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.repaymentAccountBalance,
        0
      );
      let investmentAmountBasis = combinedInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.basisAccountBalance,
        0
      );
      let sourceAmountRegular =
        (sourceAmount * investmentAmountRegular) / investmentAmount;
      let remainingAmountRegular = sourceAmountRegular;
      let sourceAmountRepayment =
        (sourceAmount * investmentAmountRepayment) / investmentAmount;
      let remainingAmountRepayment = sourceAmountRepayment;
      let sourceAmountBasis =
        (sourceAmount * investmentAmountBasis) / investmentAmount;
      let remainingAmountBasis = sourceAmountBasis;

      for (
        let investmentIndex = 0;
        investmentIndex < combinedInvestments.length;
        investmentIndex++
      ) {
        let investment = { ...combinedInvestments[investmentIndex] };
        let investmentBalance = investment.sourceEmployeeInvestmentMap;
        let multiplierRegular = getValues(
          (investmentBalance.regularAccountBalance / investmentAmountRegular) *
            100
        );
        if (investmentAmountRegular <= sourceAmountRegular)
          investmentWithdrawalAmountRegular =
            (multiplierRegular * sourceAmountRegular) / 100;
        else
          investmentWithdrawalAmountRegular =
            (multiplierRegular * sourceAmountRegular) / 100;

        let multiplierRepayment = getValues(
          (investmentBalance.repaymentAccountBalance /
            investmentAmountRepayment) *
            100
        );
        if (investmentAmountRepayment <= sourceAmountRepayment)
          investmentWithdrawalAmountRepayment =
            (multiplierRepayment * sourceAmountRepayment) / 100;
        else
          investmentWithdrawalAmountRepayment =
            (multiplierRepayment * sourceAmountRepayment) / 100;

        let multiplierBasis = getValues(
          (investmentBalance.basisAccountBalance / investmentAmountBasis) * 100
        );
        if (investmentAmountBasis <= sourceAmountBasis)
          investmentWithdrawalAmountBasis =
            (multiplierBasis * sourceAmountBasis) / 100;
        else
          investmentWithdrawalAmountBasis =
            (multiplierBasis * sourceAmountBasis) / 100;

        remainingAmountRegular -= investmentWithdrawalAmountRegular;
        remainingAmountRepayment -= investmentWithdrawalAmountRepayment;
        remainingAmountBasis -= investmentWithdrawalAmountBasis;

        let newInvestmentMap = {};
        newInvestmentMap.accountBalance =
          investmentWithdrawalAmountRegular +
          investmentWithdrawalAmountRepayment +
          investmentWithdrawalAmountBasis;
        newInvestmentMap.regularAccountBalance = investmentWithdrawalAmountRegular;
        newInvestmentMap.repaymentAccountBalance = investmentWithdrawalAmountRepayment;
        newInvestmentMap.basisAccountBalance = investmentWithdrawalAmountBasis;
        newInvestmentMap.regularCCBBalance =
          investmentBalance.regularAccountBalance == 0
            ? 0
            : (investmentBalance.regularCCBBalance /
                investmentBalance.regularAccountBalance) *
              newInvestmentMap.regularAccountBalance;
        newInvestmentMap.regularEarningsBalance =
          investmentBalance.regularAccountBalance == 0
            ? 0
            : (investmentBalance.regularEarningsBalance /
                investmentBalance.regularAccountBalance) *
              newInvestmentMap.regularAccountBalance;
        newInvestmentMap.repaymentCCBBalance =
          investmentBalance.repaymentAccountBalance == 0
            ? 0
            : (investmentBalance.repaymentCCBBalance /
                investmentBalance.repaymentAccountBalance) *
              newInvestmentMap.repaymentAccountBalance;
        newInvestmentMap.repaymentEarningsBalance =
          investmentBalance.repaymentAccountBalance == 0
            ? 0
            : (investmentBalance.repaymentEarningsBalance /
                investmentBalance.repaymentAccountBalance) *
              newInvestmentMap.repaymentAccountBalance;
        newInvestmentMap.basisCCBBalance =
          investmentBalance.basisAccountBalance == 0
            ? 0
            : (investmentBalance.basisCCBBalance /
                investmentBalance.basisAccountBalance) *
              newInvestmentMap.basisAccountBalance;
        newInvestmentMap.basisEarningsBalance =
          investmentBalance.basisAccountBalance == 0
            ? 0
            : (investmentBalance.basisEarningsBalance /
                investmentBalance.basisAccountBalance) *
              newInvestmentMap.basisAccountBalance;

        let newInvestment = { ...investment };
        newInvestment.sourceEmployeeInvestmentMap = newInvestmentMap;
        investmentList.push(newInvestment);
      }
      sourceAmountRegular = remainingAmountRegular;
      sourceAmountRepayment = remainingAmountRepayment;
      sourceAmountBasis = remainingAmountBasis;
    }
  }
  return investmentList;
}

export function CalculateInvestmentHierarchySplit(
  tempEmployeeSourceBalances,
  hierarchy,
  withdrawalAmount
) {
  const employeeSourceBalances = cloneDeep(tempEmployeeSourceBalances);
  let sourceList = [];
  let totalAccountBalance = employeeSourceBalances?.reduce(
    (a, b) => a + b.sourceEmployeeMap.accountBalance,
    0
  );
  employeeSourceBalances.forEach((source) => {
    let newSource = { ...source };
    let newSourceEmployeeMap = {};
    let investmentList = [];
    let sourceBalance = { ...source.sourceEmployeeMap };
    if (sourceBalance.accountBalance != 0) {
      newSourceEmployeeMap.accountBalance = parseFloat(
        (sourceBalance.accountBalance / totalAccountBalance) * withdrawalAmount
      );
      newSourceEmployeeMap.regularAccountBalance =
        (sourceBalance.regularAccountBalance / sourceBalance.accountBalance) *
        newSourceEmployeeMap.accountBalance;
      newSourceEmployeeMap.repaymentAccountBalance =
        (sourceBalance.repaymentAccountBalance / sourceBalance.accountBalance) *
        newSourceEmployeeMap.accountBalance;
      newSourceEmployeeMap.basisAccountBalance =
        (sourceBalance.basisAccountBalance / sourceBalance.accountBalance) *
        newSourceEmployeeMap.accountBalance;
      newSourceEmployeeMap.regularCCBBalance =
        sourceBalance.regularAccountBalance == 0
          ? 0
          : (sourceBalance.regularCCBBalance /
              sourceBalance.regularAccountBalance) *
            newSourceEmployeeMap.regularAccountBalance;
      newSourceEmployeeMap.regularEarningsBalance =
        sourceBalance.regularAccountBalance == 0
          ? 0
          : (sourceBalance.regularEarningsBalance /
              sourceBalance.regularAccountBalance) *
            newSourceEmployeeMap.regularAccountBalance;
      newSourceEmployeeMap.repaymentCCBBalance =
        sourceBalance.repaymentAccountBalance == 0
          ? 0
          : (sourceBalance.repaymentCCBBalance /
              sourceBalance.repaymentAccountBalance) *
            newSourceEmployeeMap.repaymentAccountBalance;
      newSourceEmployeeMap.repaymentEarningsBalance =
        sourceBalance.repaymentAccountBalance == 0
          ? 0
          : (sourceBalance.repaymentEarningsBalance /
              sourceBalance.repaymentAccountBalance) *
            newSourceEmployeeMap.repaymentAccountBalance;
      newSourceEmployeeMap.basisCCBBalance =
        sourceBalance.basisAccountBalance == 0
          ? 0
          : (sourceBalance.basisCCBBalance /
              sourceBalance.basisAccountBalance) *
            newSourceEmployeeMap.basisAccountBalance;
      newSourceEmployeeMap.basisEarningsBalance =
        sourceBalance.basisAccountBalance == 0
          ? 0
          : (sourceBalance.basisEarningsBalance /
              sourceBalance.basisAccountBalance) *
            newSourceEmployeeMap.basisAccountBalance;

      investmentList.push(
        ...InvestmentHierarchySplit(
          hierarchy,
          source,
          newSourceEmployeeMap.accountBalance
        )
      );
    }
    newSource.sourceInvestmentBalances = investmentList;
    newSource.sourceEmployeeMap = {
      ...newSourceEmployeeMap,
      sourceEmployeeInvestmentMaps: investmentList,
    };
    sourceList.push(newSource);
  });
  return sourceList;
}

export function CalculateSourceHierarchyInvestmentHierarchySplit(
  employeeSourceBalances,
  hierarchy_,
  requestedAmount
) {
  let sourceList = [];
  let hierarchy = cloneDeep(hierarchy_);
  let maxHierarchy = hierarchy?.sort(
    (a, b) => b?.hierarchyNumber - a?.hierarchyNumber
  )[0]?.hierarchyNumber;
  let amount = requestedAmount;
  for (let i = 1; i <= maxHierarchy; i++) {
    let sourceWithdrawalAmount = 0;
    let investmentSources = hierarchy.filter((_) => _.hierarchyNumber === i);
    let source = employeeSourceBalances.filter((_) =>
      investmentSources.map((x) => x.sourceId).includes(_.sourceId)
    );

    if (source == null) continue;
    let totalSourceAmount =
      source?.reduce((a, b) => a + b.sourceEmployeeMap.accountBalance, 0) ?? 0;
    if (source == null) continue;
    let remainingAmount = amount;
    for (let n_ in source) {
      let n = { ...source[n_] };
      let newSource = n;
      let newSourceEmployeeMap = {};
      let sourceBalance = n.sourceEmployeeMap;
      if (totalSourceAmount <= amount)
        sourceWithdrawalAmount = n.sourceEmployeeMap.accountBalance;
      else
        sourceWithdrawalAmount =
          (n.sourceEmployeeMap.accountBalance / totalSourceAmount) * amount;

      remainingAmount -= sourceWithdrawalAmount;

      newSourceEmployeeMap.accountBalance = sourceWithdrawalAmount;
      newSourceEmployeeMap.regularAccountBalance =
        (sourceBalance.regularAccountBalance / sourceBalance.accountBalance) *
        newSourceEmployeeMap.accountBalance;
      newSourceEmployeeMap.repaymentAccountBalance =
        (sourceBalance.repaymentAccountBalance / sourceBalance.accountBalance) *
        newSourceEmployeeMap.accountBalance;
      newSourceEmployeeMap.basisAccountBalance =
        (sourceBalance.basisAccountBalance / sourceBalance.accountBalance) *
        newSourceEmployeeMap.accountBalance;
      newSourceEmployeeMap.regularCCBBalance =
        sourceBalance.regularAccountBalance == 0
          ? 0
          : (sourceBalance.regularCCBBalance /
              sourceBalance.regularAccountBalance) *
            newSourceEmployeeMap.regularAccountBalance;
      newSourceEmployeeMap.regularEarningsBalance =
        sourceBalance.regularAccountBalance == 0
          ? 0
          : (sourceBalance.regularEarningsBalance /
              sourceBalance.regularAccountBalance) *
            newSourceEmployeeMap.regularAccountBalance;
      newSourceEmployeeMap.repaymentCCBBalance =
        sourceBalance.repaymentAccountBalance == 0
          ? 0
          : (sourceBalance.repaymentCCBBalance /
              sourceBalance.repaymentAccountBalance) *
            newSourceEmployeeMap.repaymentAccountBalance;
      newSourceEmployeeMap.repaymentEarningsBalance =
        sourceBalance.repaymentAccountBalance == 0
          ? 0
          : (sourceBalance.repaymentEarningsBalance /
              sourceBalance.repaymentAccountBalance) *
            newSourceEmployeeMap.repaymentAccountBalance;
      newSourceEmployeeMap.basisCCBBalance =
        sourceBalance.basisAccountBalance == 0
          ? 0
          : (sourceBalance.basisCCBBalance /
              sourceBalance.basisAccountBalance) *
            newSourceEmployeeMap.basisAccountBalance;
      newSourceEmployeeMap.basisEarningsBalance =
        sourceBalance.basisAccountBalance == 0
          ? 0
          : (sourceBalance.basisEarningsBalance /
              sourceBalance.basisAccountBalance) *
            newSourceEmployeeMap.basisAccountBalance;
      const sourceInvestmentBalances_ = InvestmentHierarchySplit(
        hierarchy,
        n,
        newSourceEmployeeMap.accountBalance
      );
      newSource.sourceInvestmentBalances = sourceInvestmentBalances_;
      newSource.sourceEmployeeMap = {
        ...newSourceEmployeeMap,
        sourceEmployeeInvestmentMaps: sourceInvestmentBalances_,
      };
      sourceList.push(newSource);
    }
    amount = remainingAmount;
  }
  return sourceList;
}

/////Gross Amount Split

export function CalculateGrossAmountSplit(
  tempEmployeeSourceBalances,
  withDrawalType,
  feeAndTaxes,
  withdrawalStrategy,
  hierarchy_,
  requestedAmount,
  sourceWiseRequestAmount,
  withdrawalRequestData
) {
  let hierarchy = cloneDeep(hierarchy_);
  const employeeSourceBalances = cloneDeep(tempEmployeeSourceBalances);
  let netAvailableAmount = CalculateNetAvailableAmountSplit(
    employeeSourceBalances,
    withDrawalType,
    feeAndTaxes,
    sourceWiseRequestAmount
  );
  let netWithdrawalAmount = GetWithdrawalAmountBasedOnStrategy(
    withdrawalStrategy,
    netAvailableAmount.availableNetAmountSplit,
    hierarchy,
    requestedAmount,
    sourceWiseRequestAmount
  );

  if (
    calculationToBeBasedOnGrossAmount(withdrawalRequestData) &&
    withdrawalStrategy !== WithdrawalStrategy.AllowMeToChoose
  ) {
    return GetWithdrawalAmountBasedOnStrategy(
      withdrawalStrategy,
      employeeSourceBalances,
      hierarchy,
      requestedAmount,
      sourceWiseRequestAmount
    );
  }

  let taxes = CalculateTaxSplitUpForGrossAmount(
    netWithdrawalAmount,
    feeAndTaxes
  );
  let grossAmount = AddProratedSplits(taxes, netAvailableAmount.feesSplit);

  // let grossAmount = AddProratedSplits(taxes, netAvailableAmount.feesSplit);
  return grossAmount;
}

function CalculateUpdatedBalances(tempEmployeeSourceBalances, withDrawalType) {
  const employeeSourceBalances = cloneDeep(tempEmployeeSourceBalances);
  let updatedSourceBalances = [];
  for (let source_ in employeeSourceBalances) {
    let source = { ...employeeSourceBalances[source_] };
    let restrictedType = GetRestrictedType(withDrawalType);

    let investments = [
      ...source.sourceInvestmentBalances.filter(
        (_) =>
          !(
            get(_, "restriction.applicableTypes", []).includes(
              restrictedType
            ) &&
            moment(_.restriction?.effectiveStartDate) <=
              moment().format("MM/DD/YYYY") &&
            moment(_.restriction?.effectiveEndDate) >=
              moment().format("MM/DD/YYYY")
          )
      ),
    ];
    let tempInvestments = [];
    investments.forEach((i) => {
      let inv = { ...i };
      inv.sourceEmployeeInvestmentMap.accountBalance =
        inv.sourceEmployeeInvestmentMap.regularAccountBalance +
        inv.sourceEmployeeInvestmentMap.repaymentAccountBalance +
        inv.sourceEmployeeInvestmentMap.basisAccountBalance;
      tempInvestments.push(inv);
    });
    let newEmployeeMap = {
      sourceId: source.sourceId,
      sourceName: source.sourceName,
      repaymentCCBBalance: tempInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.repaymentCCBBalance,
        0
      ),
      regularCCBBalance: tempInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.regularCCBBalance,
        0
      ),
      basisCCBBalance: tempInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.basisCCBBalance,
        0
      ),
      repaymentAccountBalance: tempInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.repaymentAccountBalance,
        0
      ),
      regularAccountBalance: tempInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.regularAccountBalance,
        0
      ),
      basisAccountBalance: tempInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.basisAccountBalance,
        0
      ),
      repaymentEarningsBalance: tempInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.repaymentEarningsBalance,
        0
      ),
      regularEarningsBalance: tempInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.regularEarningsBalance,
        0
      ),
      basisEarningsBalance: tempInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.basisEarningsBalance,
        0
      ),
      accountBalance: tempInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.accountBalance,
        0
      ),
      vestedBalance: tempInvestments?.reduce(
        (a, b) => a + b.sourceEmployeeInvestmentMap.accountBalance,
        0
      ),
      sourceEmployeeInvestmentMaps: tempInvestments,
    };
    source.sourceEmployeeMap = newEmployeeMap;
    source.sourceInvestmentBalances = tempInvestments;
    updatedSourceBalances.push(source);
  }
  return updatedSourceBalances;
}
export const taxCalculationForNetForEachSource = (
  source,
  taxes,
  applyOnlyTax,
  applyOnlyPenalty
) => {
  let newSource = { ...source };
  let sourceBalance = source.sourceEmployeeMap;
  let newSourceEmployeeMap = {};
  let taxAndPenalty =
    taxes
      .filter(
        (_) =>
          (!applyOnlyTax &&
            _.withdrawalAmountType == WithdrawalAmountType.Penalty) ||
          (!applyOnlyPenalty &&
            _.withdrawalAmountType == WithdrawalAmountType.StateTax) ||
          (!applyOnlyPenalty &&
            _.withdrawalAmountType == WithdrawalAmountType.FederalTax)
      )
      ?.reduce((a, b) => a + b.amount, 0) / 100;
  let penalty = applyOnlyTax
    ? 0
    : (taxes.find((_) => _.withdrawalAmountType == WithdrawalAmountType.Penalty)
        ?.amount ?? 0) / 100;
  if (
    source.sourceSubCategory != null &&
    [
      SourceSubCategory.EmployeeAfterTax,
      SourceSubCategory.EmployeeAfterTaxRollover,
      SourceSubCategory.EmployeeRoth,
      SourceSubCategory.EmployeeRothRollover,
    ].includes(source.sourceSubCategory)
  ) {
    newSourceEmployeeMap.regularCCBBalance =
      sourceBalance.regularCCBBalance * (1 - penalty);
    newSourceEmployeeMap.repaymentCCBBalance =
      sourceBalance.repaymentCCBBalance * (1 - penalty);
    // newSourceEmployeeMap.basisCCBBalance =
    //   sourceBalance.basisCCBBalance * (1 - penalty);
  } else {
    newSourceEmployeeMap.regularCCBBalance =
      sourceBalance.regularCCBBalance * (1 - taxAndPenalty);
    newSourceEmployeeMap.repaymentCCBBalance =
      sourceBalance.repaymentCCBBalance * (1 - taxAndPenalty);
    // newSourceEmployeeMap.basisCCBBalance =
    //   sourceBalance.basisCCBBalance * (1 - taxAndPenalty);
  }
  if (
    source.sourceSubCategory != null &&
    [
      SourceSubCategory.EmployeeRoth,
      SourceSubCategory.EmployeeRothRollover,
    ].includes(source.sourceSubCategory) &&
    source.isNotTaxable
  ) {
    newSourceEmployeeMap.regularEarningsBalance =
      sourceBalance.regularEarningsBalance * (1 - penalty);
    newSourceEmployeeMap.repaymentEarningsBalance =
      sourceBalance.repaymentEarningsBalance * (1 - penalty);
    newSourceEmployeeMap.basisEarningsBalance =
      sourceBalance.basisEarningsBalance * (1 - penalty);
  } else {
    newSourceEmployeeMap.regularEarningsBalance =
      sourceBalance.regularEarningsBalance * (1 - taxAndPenalty);
    newSourceEmployeeMap.repaymentEarningsBalance =
      sourceBalance.repaymentEarningsBalance * (1 - taxAndPenalty);
    newSourceEmployeeMap.basisEarningsBalance =
      sourceBalance.basisEarningsBalance * (1 - taxAndPenalty);
  }
  newSourceEmployeeMap.basisCCBBalance =
    sourceBalance.basisCCBBalance * (1 - penalty);
  newSourceEmployeeMap.regularAccountBalance =
    newSourceEmployeeMap.regularCCBBalance +
    newSourceEmployeeMap.regularEarningsBalance;
  newSourceEmployeeMap.repaymentAccountBalance =
    newSourceEmployeeMap.repaymentCCBBalance +
    newSourceEmployeeMap.repaymentEarningsBalance;
  newSourceEmployeeMap.basisAccountBalance =
    newSourceEmployeeMap.basisCCBBalance +
    newSourceEmployeeMap.basisEarningsBalance;
  newSourceEmployeeMap.accountBalance =
    newSourceEmployeeMap.regularAccountBalance +
    newSourceEmployeeMap.repaymentAccountBalance +
    newSourceEmployeeMap.basisAccountBalance;
  newSourceEmployeeMap.sourceWithdrawalAmount =
    newSourceEmployeeMap.accountBalance;

  let investmentList = [];
  source.sourceInvestmentBalances.forEach((investment) => {
    let newInv = { ...investment };
    let investmentBalance = investment.sourceEmployeeInvestmentMap;
    let newInvestmentMap = {};
    if (
      source.sourceSubCategory != null &&
      [
        SourceSubCategory.EmployeeAfterTax,
        SourceSubCategory.EmployeeAfterTaxRollover,
        SourceSubCategory.EmployeeRoth,
        SourceSubCategory.EmployeeRothRollover,
      ].includes(source.sourceSubCategory)
    ) {
      newInvestmentMap.regularCCBBalance =
        investmentBalance.regularCCBBalance * (1 - penalty);
      newInvestmentMap.repaymentCCBBalance =
        investmentBalance.repaymentCCBBalance * (1 - penalty);
    } else {
      newInvestmentMap.regularCCBBalance =
        investmentBalance.regularCCBBalance * (1 - taxAndPenalty);
      newInvestmentMap.repaymentCCBBalance =
        investmentBalance.repaymentCCBBalance * (1 - taxAndPenalty);
    }

    if (
      source.sourceSubCategory != null &&
      [
        SourceSubCategory.EmployeeRoth,
        SourceSubCategory.EmployeeRothRollover,
      ].includes(source.sourceSubCategory) &&
      source.isNotTaxable
    ) {
      newInvestmentMap.regularEarningsBalance =
        investmentBalance.regularEarningsBalance * (1 - penalty);
      newInvestmentMap.repaymentEarningsBalance =
        investmentBalance.repaymentEarningsBalance * (1 - penalty);
      newInvestmentMap.basisEarningsBalance =
        investmentBalance.basisEarningsBalance * (1 - penalty);
    } else {
      newInvestmentMap.regularEarningsBalance =
        investmentBalance.regularEarningsBalance * (1 - taxAndPenalty);
      newInvestmentMap.repaymentEarningsBalance =
        investmentBalance.repaymentEarningsBalance * (1 - taxAndPenalty);
      newInvestmentMap.basisEarningsBalance =
        investmentBalance.basisEarningsBalance * (1 - taxAndPenalty);
    }
    newInvestmentMap.basisCCBBalance =
      investmentBalance.basisCCBBalance * (1 - penalty);
    newInvestmentMap.regularAccountBalance =
      newInvestmentMap.regularCCBBalance +
      newInvestmentMap.regularEarningsBalance;
    newInvestmentMap.repaymentAccountBalance =
      newInvestmentMap.repaymentCCBBalance +
      newInvestmentMap.repaymentEarningsBalance;
    newInvestmentMap.basisAccountBalance =
      newInvestmentMap.basisCCBBalance + newInvestmentMap.basisEarningsBalance;
    newInvestmentMap.accountBalance =
      newInvestmentMap.regularAccountBalance +
      newInvestmentMap.repaymentAccountBalance +
      newInvestmentMap.basisAccountBalance;

    newInv.sourceEmployeeInvestmentMap = newInvestmentMap;
    investmentList.push(newInv);
  });
  newSource.sourceInvestmentBalances = investmentList;
  newSource.sourceEmployeeMap = newSourceEmployeeMap;
  return newSource;
};

export function CalculateTaxSplitUpForNetAmount(
  tempEmployeeSourceBalances,
  taxes
) {
  const employeeSourceBalances = cloneDeep(tempEmployeeSourceBalances);
  let employeeSources = [];
  employeeSourceBalances.forEach((source) => {
    employeeSources.push(taxCalculationForNetForEachSource(source, taxes));
  });
  return employeeSources;
}

export function CalculateNetAvailableAmountSplit(
  tempEmployeeSourceBalances,
  withDrawalType,
  feeAndTaxes,
  sourceWiseRequestAmount
) {
  const employeeSourceBalances = cloneDeep(tempEmployeeSourceBalances);
  let updatedSourceBalances = CalculateUpdatedBalances(
    employeeSourceBalances,
    withDrawalType
  );
  let totalFees = CalculateTotalFees(feeAndTaxes);
  let feesProration = CalculateProrationSplitUp(
    updatedSourceBalances,
    totalFees,
    sourceWiseRequestAmount,
    false
  );
  let balances = DeductProratedSplits(updatedSourceBalances, feesProration, 1);
  let netAmountAvailable = CalculateTaxSplitUpForNetAmount(
    balances,
    feeAndTaxes
  );
  let result = {
    feesSplit: feesProration,
    availableNetAmountSplit: netAmountAvailable,
  };
  return result;
}

export function CalculateTotalFees(fees) {
  return fees
    .filter(
      (_) =>
        _.withdrawalAmountType == WithdrawalAmountType.RollOverFee ||
        _.withdrawalAmountType == WithdrawalAmountType.WithdrawalFee ||
        _.withdrawalAmountType == WithdrawalAmountType.ChequeFee ||
        _.withdrawalAmountType == WithdrawalAmountType.EFTFee
    )
    ?.reduce((a, b) => a + b.amount, 0);
}

export function DeductProratedSplits(
  initialBalances_,
  proratedBalances_,
  multiplierToReduceBy = 1
) {
  const initialBalances = cloneDeep(initialBalances_);
  const proratedBalances = cloneDeep(proratedBalances_);
  let sourceList = [];
  initialBalances.forEach((source) => {
    let newSource = { ...source };
    let sourceBalance = source.sourceEmployeeMap;
    let proratedSource = proratedBalances.filter(
      (_) => _.sourceId == source.sourceId
    )[0];
    let proratedSourceMap = proratedSource?.sourceEmployeeMap;
    let newSourceMap = {};

    if (sourceBalance.accountBalance && proratedSourceMap) {
      newSourceMap.regularAccountBalance =
        (sourceBalance.regularAccountBalance -
          proratedSourceMap.regularAccountBalance) *
        multiplierToReduceBy;
      newSourceMap.repaymentAccountBalance =
        (sourceBalance.repaymentAccountBalance -
          proratedSourceMap.repaymentAccountBalance) *
        multiplierToReduceBy;
      newSourceMap.basisAccountBalance =
        (sourceBalance.basisAccountBalance -
          proratedSourceMap.basisAccountBalance) *
        multiplierToReduceBy;
      newSourceMap.regularCCBBalance =
        (sourceBalance.regularCCBBalance -
          proratedSourceMap.regularCCBBalance) *
        multiplierToReduceBy;
      newSourceMap.regularEarningsBalance =
        (sourceBalance.regularEarningsBalance -
          proratedSourceMap.regularEarningsBalance) *
        multiplierToReduceBy;
      newSourceMap.repaymentCCBBalance =
        (sourceBalance.repaymentCCBBalance -
          proratedSourceMap.repaymentCCBBalance) *
        multiplierToReduceBy;
      newSourceMap.repaymentEarningsBalance =
        (sourceBalance.repaymentEarningsBalance -
          proratedSourceMap.repaymentEarningsBalance) *
        multiplierToReduceBy;
      newSourceMap.basisCCBBalance =
        (sourceBalance.basisCCBBalance - proratedSourceMap.basisCCBBalance) *
        multiplierToReduceBy;
      newSourceMap.basisEarningsBalance =
        (sourceBalance.basisEarningsBalance -
          proratedSourceMap.basisEarningsBalance) *
        multiplierToReduceBy;
      newSourceMap.accountBalance =
        newSourceMap.regularAccountBalance +
        newSourceMap.repaymentAccountBalance +
        newSourceMap.basisAccountBalance;

      let investmentList = [];

      source.sourceInvestmentBalances.forEach((investment) => {
        let newInvestmentMap = {};
        let investmentBalance = investment.sourceEmployeeInvestmentMap;
        let proratedInvestment = proratedSource.sourceInvestmentBalances.filter(
          (_) => _.investmentId == investment.investmentId
        )[0]?.sourceEmployeeInvestmentMap;
        let newInvestment = { ...investment };
        newInvestmentMap.regularAccountBalance =
          (getValues(investmentBalance?.regularAccountBalance) -
            getValues(proratedInvestment?.regularAccountBalance)) *
          multiplierToReduceBy;
        newInvestmentMap.repaymentAccountBalance =
          (getValues(investmentBalance?.repaymentAccountBalance) -
            getValues(proratedInvestment?.repaymentAccountBalance)) *
          multiplierToReduceBy;
        newInvestmentMap.basisAccountBalance =
          (getValues(investmentBalance?.basisAccountBalance) -
            getValues(proratedInvestment?.basisAccountBalance)) *
          multiplierToReduceBy;
        newInvestmentMap.regularCCBBalance =
          (getValues(investmentBalance?.regularCCBBalance) -
            getValues(proratedInvestment?.regularCCBBalance)) *
          multiplierToReduceBy;
        newInvestmentMap.regularEarningsBalance =
          (getValues(investmentBalance?.regularEarningsBalance) -
            getValues(proratedInvestment?.regularEarningsBalance)) *
          multiplierToReduceBy;
        newInvestmentMap.repaymentCCBBalance =
          (getValues(investmentBalance?.repaymentCCBBalance) -
            getValues(proratedInvestment?.repaymentCCBBalance)) *
          multiplierToReduceBy;
        newInvestmentMap.repaymentEarningsBalance =
          (getValues(investmentBalance?.repaymentEarningsBalance) -
            getValues(proratedInvestment?.repaymentEarningsBalance)) *
          multiplierToReduceBy;
        newInvestmentMap.basisCCBBalance =
          (getValues(investmentBalance?.basisCCBBalance) -
            getValues(proratedInvestment?.basisCCBBalance)) *
          multiplierToReduceBy;
        newInvestmentMap.basisEarningsBalance =
          (getValues(investmentBalance?.basisEarningsBalance) -
            getValues(proratedInvestment?.basisEarningsBalance)) *
          multiplierToReduceBy;
        newInvestmentMap.accountBalance =
          newInvestmentMap.regularAccountBalance +
          newInvestmentMap.repaymentAccountBalance +
          newInvestmentMap.basisAccountBalance;

        newInvestment.sourceEmployeeInvestmentMap = newInvestmentMap;
        investmentList.push(newInvestment);
      });
      newSource.sourceInvestmentBalances = investmentList;
    }
    newSource.sourceEmployeeMap = newSourceMap;
    sourceList.push(newSource);
  });
  return sourceList;
}

export const taxCalculationForGrossForEachSource = (source, taxes) => {
  let newSource = cloneDeep(source);
  let sourceBalance = source.sourceEmployeeMap;
  let newSourceEmployeeMap = {};
  let taxAndPenalty =
    taxes
      .filter(
        (_) =>
          _.withdrawalAmountType == WithdrawalAmountType.Penalty ||
          _.withdrawalAmountType == WithdrawalAmountType.StateTax ||
          _.withdrawalAmountType == WithdrawalAmountType.FederalTax
      )
      ?.reduce((a, b) => a + b.amount, 0) / 100;
  let penalty =
    (taxes.find((_) => _.withdrawalAmountType == WithdrawalAmountType.Penalty)
      ?.amount ?? 0) / 100;

  if (
    source.sourceSubCategory != null &&
    [
      SourceSubCategory.EmployeeAfterTax,
      SourceSubCategory.EmployeeAfterTaxRollover,
      SourceSubCategory.EmployeeRoth,
      SourceSubCategory.EmployeeRothRollover,
    ].includes(source.sourceSubCategory)
  ) {
    newSourceEmployeeMap.regularCCBBalance =
      sourceBalance.regularCCBBalance / (1 - penalty);
    newSourceEmployeeMap.repaymentCCBBalance =
      sourceBalance.repaymentCCBBalance / (1 - penalty);
  } else {
    newSourceEmployeeMap.regularCCBBalance =
      sourceBalance.regularCCBBalance / (1 - taxAndPenalty);
    newSourceEmployeeMap.repaymentCCBBalance =
      sourceBalance.repaymentCCBBalance / (1 - taxAndPenalty);
  }
  if (
    source.sourceSubCategory != null &&
    [
      SourceSubCategory.EmployeeRoth,
      SourceSubCategory.EmployeeRothRollover,
    ].includes(source.sourceSubCategory) &&
    source.isNotTaxable
  ) {
    newSourceEmployeeMap.regularEarningsBalance =
      sourceBalance.regularEarningsBalance / (1 - penalty);
    newSourceEmployeeMap.repaymentEarningsBalance =
      sourceBalance.repaymentEarningsBalance / (1 - penalty);
    newSourceEmployeeMap.basisEarningsBalance =
      sourceBalance.basisEarningsBalance / (1 - penalty);
  } else {
    newSourceEmployeeMap.regularEarningsBalance =
      sourceBalance.regularEarningsBalance / (1 - taxAndPenalty);
    newSourceEmployeeMap.repaymentEarningsBalance =
      sourceBalance.repaymentEarningsBalance / (1 - taxAndPenalty);
    newSourceEmployeeMap.basisEarningsBalance =
      sourceBalance.basisEarningsBalance / (1 - taxAndPenalty);
  }
  newSourceEmployeeMap.basisCCBBalance =
    sourceBalance.basisCCBBalance / (1 - penalty);
  newSourceEmployeeMap.regularAccountBalance =
    newSourceEmployeeMap.regularCCBBalance +
    newSourceEmployeeMap.regularEarningsBalance;
  newSourceEmployeeMap.repaymentAccountBalance =
    newSourceEmployeeMap.repaymentCCBBalance +
    newSourceEmployeeMap.repaymentEarningsBalance;
  newSourceEmployeeMap.basisAccountBalance =
    newSourceEmployeeMap.basisCCBBalance +
    newSourceEmployeeMap.basisEarningsBalance;
  newSourceEmployeeMap.accountBalance =
    newSourceEmployeeMap.regularAccountBalance +
    newSourceEmployeeMap.repaymentAccountBalance +
    newSourceEmployeeMap.basisAccountBalance;

  let investmentList = [];
  source.sourceInvestmentBalances.forEach((investment) => {
    let newInv = { ...investment };
    let investmentBalance = investment.sourceEmployeeInvestmentMap;
    let newInvestmentMap = {};
    if (
      source.sourceSubCategory != null &&
      [
        SourceSubCategory.EmployeeAfterTax,
        SourceSubCategory.EmployeeAfterTaxRollover,
        SourceSubCategory.EmployeeRoth,
        SourceSubCategory.EmployeeRothRollover,
      ].includes(source.sourceSubCategory)
    ) {
      newInvestmentMap.regularCCBBalance =
        investmentBalance.regularCCBBalance / (1 - penalty);
      newInvestmentMap.repaymentCCBBalance =
        investmentBalance.repaymentCCBBalance / (1 - penalty);
    } else {
      newInvestmentMap.regularCCBBalance =
        investmentBalance.regularCCBBalance / (1 - taxAndPenalty);
      newInvestmentMap.repaymentCCBBalance =
        investmentBalance.repaymentCCBBalance / (1 - taxAndPenalty);
    }
    if (
      source.sourceSubCategory != null &&
      [
        SourceSubCategory.EmployeeRoth,
        SourceSubCategory.EmployeeRothRollover,
      ].includes(source.sourceSubCategory) &&
      source.isNotTaxable
    ) {
      newInvestmentMap.regularEarningsBalance =
        investmentBalance.regularEarningsBalance / (1 - penalty);
      newInvestmentMap.repaymentEarningsBalance =
        investmentBalance.repaymentEarningsBalance / (1 - penalty);
      newInvestmentMap.basisEarningsBalance =
        investmentBalance.basisEarningsBalance / (1 - penalty);
    } else {
      newInvestmentMap.regularEarningsBalance =
        investmentBalance.regularEarningsBalance / (1 - taxAndPenalty);
      newInvestmentMap.repaymentEarningsBalance =
        investmentBalance.repaymentEarningsBalance / (1 - taxAndPenalty);
      newInvestmentMap.basisEarningsBalance =
        investmentBalance.basisEarningsBalance / (1 - taxAndPenalty);
    }
    newInvestmentMap.basisCCBBalance =
      investmentBalance.basisCCBBalance / (1 - penalty);
    newInvestmentMap.regularAccountBalance =
      newInvestmentMap.regularCCBBalance +
      newInvestmentMap.regularEarningsBalance;
    newInvestmentMap.repaymentAccountBalance =
      newInvestmentMap.repaymentCCBBalance +
      newInvestmentMap.repaymentEarningsBalance;
    newInvestmentMap.basisAccountBalance =
      newInvestmentMap.basisCCBBalance + newInvestmentMap.basisEarningsBalance;
    newInvestmentMap.accountBalance =
      newInvestmentMap.regularAccountBalance +
      newInvestmentMap.repaymentAccountBalance +
      newInvestmentMap.basisAccountBalance;

    newInv.sourceEmployeeInvestmentMap = newInvestmentMap;
    investmentList.push(newInv);
  });
  newSource.sourceInvestmentBalances = investmentList;
  newSource.sourceEmployeeMap = newSourceEmployeeMap;
  return newSource;
};

export function CalculateTaxSplitUpForGrossAmount(
  tempEmployeeSourceBalances,
  taxes
) {
  const employeeSourceBalances = cloneDeep(tempEmployeeSourceBalances);
  let employeeSources = [];
  employeeSourceBalances.forEach((source) => {
    employeeSources.push(taxCalculationForGrossForEachSource(source, taxes));
  });
  return employeeSources;
}

export function AddProratedSplits(initialBalances_, proratedBalances) {
  let initialBalances = [...initialBalances_];
  let sourceList = [];

  initialBalances.forEach((source) => {
    let newSource = { ...source };
    let sourceBalance = source.sourceEmployeeMap;
    let proratedSource = proratedBalances.filter(
      (_) => _.sourceId == source.sourceId
    )[0];
    let proratedSourceMap = proratedSource?.sourceEmployeeMap;
    let newSourceMap = {};
    if (true) {
      newSourceMap.accountBalance =
        sourceBalance.accountBalance + proratedSourceMap.accountBalance;
      newSourceMap.regularAccountBalance =
        sourceBalance.regularAccountBalance +
        proratedSourceMap.regularAccountBalance;
      newSourceMap.repaymentAccountBalance =
        sourceBalance.repaymentAccountBalance +
        proratedSourceMap.repaymentAccountBalance;
      newSourceMap.basisAccountBalance =
        sourceBalance.basisAccountBalance +
        proratedSourceMap.basisAccountBalance;
      newSourceMap.regularCCBBalance =
        sourceBalance.regularCCBBalance + proratedSourceMap.regularCCBBalance;
      newSourceMap.regularEarningsBalance =
        sourceBalance.regularEarningsBalance +
        proratedSourceMap.regularEarningsBalance;
      newSourceMap.repaymentCCBBalance =
        sourceBalance.repaymentCCBBalance +
        proratedSourceMap.repaymentCCBBalance;
      newSourceMap.repaymentEarningsBalance =
        sourceBalance.repaymentEarningsBalance +
        proratedSourceMap.repaymentEarningsBalance;
      newSourceMap.basisCCBBalance =
        sourceBalance.basisCCBBalance + proratedSourceMap.basisCCBBalance;
      newSourceMap.basisEarningsBalance =
        sourceBalance.basisEarningsBalance +
        proratedSourceMap.basisEarningsBalance;

      let investmentList = [];

      source.sourceInvestmentBalances.forEach((investment) => {
        let investmentBalance = investment.sourceEmployeeInvestmentMap;
        let proratedInvestment = proratedSource.sourceInvestmentBalances.find(
          (_) => _.investmentId == investment.investmentId
        )?.sourceEmployeeInvestmentMap;
        let newInvestment = { ...investment };
        let newInvestmentMap = {};
        newInvestmentMap.accountBalance =
          investmentBalance.accountBalance + proratedInvestment.accountBalance;
        newInvestmentMap.regularAccountBalance =
          investmentBalance.regularAccountBalance +
          proratedInvestment.regularAccountBalance;
        newInvestmentMap.repaymentAccountBalance =
          investmentBalance.repaymentAccountBalance +
          proratedInvestment.repaymentAccountBalance;
        newInvestmentMap.basisAccountBalance =
          investmentBalance.basisAccountBalance +
          proratedInvestment.basisAccountBalance;
        newInvestmentMap.regularCCBBalance =
          investmentBalance.regularCCBBalance +
          proratedInvestment.regularCCBBalance;
        newInvestmentMap.regularEarningsBalance =
          investmentBalance.regularEarningsBalance +
          proratedInvestment.regularEarningsBalance;
        newInvestmentMap.repaymentCCBBalance =
          investmentBalance.repaymentCCBBalance +
          proratedInvestment.repaymentCCBBalance;
        newInvestmentMap.repaymentEarningsBalance =
          investmentBalance.repaymentEarningsBalance +
          proratedInvestment.repaymentEarningsBalance;
        newInvestmentMap.basisCCBBalance =
          investmentBalance.basisCCBBalance +
          proratedInvestment.basisCCBBalance;
        newInvestmentMap.basisEarningsBalance =
          investmentBalance.basisEarningsBalance +
          proratedInvestment.basisEarningsBalance;

        newInvestment.sourceEmployeeInvestmentMap = newInvestmentMap;
        investmentList.push(newInvestment);
      });
      newSource.sourceInvestmentBalances = investmentList;
    }
    newSource.sourceEmployeeMap = newSourceMap;
    sourceList.push(newSource);
  });
  return sourceList;
}

///Prorate Investment

export function ProrateInvestmentDataByBalances(
  investmentBalance,
  requestedAmount
) {
  const totalAvailableBalance =
    investmentBalance?.regularAccountBalance +
    investmentBalance?.repaymentAccountBalance +
    investmentBalance?.basisAccountBalance;

  return {
    sourceWithdrawalId: 0,
    investmentId: investmentBalance.investmentId,
    regularCCBWithdrawal:
      (requestedAmount * investmentBalance?.regularCCBBalance) /
      totalAvailableBalance,
    repaymentCCBWithdrawal:
      (requestedAmount * investmentBalance?.repaymentCCBBalance) /
      totalAvailableBalance,
    basisCCBWithdrawal:
      (requestedAmount * investmentBalance?.basisCCBBalance) /
      totalAvailableBalance,
    regularAccountWithdrawal:
      (requestedAmount * investmentBalance?.regularAccountBalance) /
      totalAvailableBalance,
    repaymentAccountWithdrawal:
      (requestedAmount * investmentBalance?.repaymentAccountBalance) /
      totalAvailableBalance,
    basisAccountWithdrawal:
      (requestedAmount * investmentBalance?.basisAccountBalance) /
      totalAvailableBalance,
    basisEarningsWithdrawal:
      (requestedAmount * investmentBalance?.basisEarningsBalance) /
      totalAvailableBalance,
    regularEarningsWithdrawal:
      (requestedAmount * investmentBalance?.regularEarningsBalance) /
      totalAvailableBalance,
    repaymentEarningsWithdrawal:
      (requestedAmount * investmentBalance?.repaymentEarningsBalance) /
      totalAvailableBalance,
    investmentWithdrawalAmount: requestedAmount,
    CCBWithdrawal:
      (requestedAmount *
        (investmentBalance?.regularCCBBalance +
          investmentBalance?.repaymentCCBBalance +
          investmentBalance?.basisCCBBalance)) /
      totalAvailableBalance,
    earningsWithdrawal:
      (requestedAmount *
        (investmentBalance?.regularEarningsBalance +
          investmentBalance?.repaymentEarningsBalance +
          investmentBalance?.basisEarningsBalance)) /
      totalAvailableBalance,
  };
}

///prorate Source

export function ProrateSourceDataByBalances(sourceBalance, requestedAmount) {
  //////not yet used
  const totalAvailableBalance =
    sourceBalance?.regularAccountBalance +
    sourceBalance?.repaymentAccountBalance +
    sourceBalance?.basisAccountBalance;

  return {
    sourceWithdrawalId: 0,
    sourceId: sourceBalance.sourceId,
    regularCCBWithdrawal:
      (requestedAmount * sourceBalance?.regularCCBBalance) /
      totalAvailableBalance,
    repaymentCCBWithdrawal:
      (requestedAmount * sourceBalance?.repaymentCCBBalance) /
      totalAvailableBalance,
    basisCCBWithdrawal:
      (requestedAmount * sourceBalance?.basisCCBBalance) /
      totalAvailableBalance,
    regularAccountWithdrawal:
      (requestedAmount * sourceBalance?.regularAccountBalance) /
      totalAvailableBalance,
    repaymentAccountWithdrawal:
      (requestedAmount * sourceBalance?.repaymentAccountBalance) /
      totalAvailableBalance,
    basisAccountWithdrawal:
      (requestedAmount * sourceBalance?.basisAccountBalance) /
      totalAvailableBalance,
    basisEarningsWithdrawal:
      (requestedAmount * sourceBalance?.basisEarningsBalance) /
      totalAvailableBalance,
    regularEarningsWithdrawal:
      (requestedAmount * sourceBalance?.regularEarningsBalance) /
      totalAvailableBalance,
    repaymentEarningsWithdrawal:
      (requestedAmount * sourceBalance?.repaymentEarningsBalance) /
      totalAvailableBalance,
    sourceWithdrawalAmount: requestedAmount,
    CCBWithdrawal:
      (requestedAmount *
        (sourceBalance?.regularCCBBalance +
          sourceBalance?.repaymentCCBBalance +
          sourceBalance?.basisCCBBalance)) /
      totalAvailableBalance,
    earningsWithdrawal:
      (requestedAmount *
        (sourceBalance?.regularEarningsBalance +
          sourceBalance?.repaymentEarningsBalance +
          sourceBalance?.basisEarningsBalance)) /
      totalAvailableBalance,
    investmentWithdrawals: sourceBalance?.sourceInvestmentBalances?.map((_) =>
      ProrateInvestmentDataByBalances(_?.sourceEmployeeInvestmentMap ?? {})
    ),
  };
}

////list investment and their available balance for investment hierarchy

export function ListInvestmentAndTheirAvailableBalance(sourceBalances) {
  const listOfSourceEmployeeInvestmentMap = flatten(
    sourceBalances?.map((_) =>
      _?.sourceInvestmentBalances.map((x) => {
        return {
          ...x?.sourceEmployeeInvestmentMap,
          accountBalance:
            parseFloat(x?.sourceEmployeeInvestmentMap?.regularAccountBalance) +
            parseFloat(
              x?.sourceEmployeeInvestmentMap?.repaymentAccountBalance
            ) +
            parseFloat(x?.sourceEmployeeInvestmentMap?.basisAccountBalance),
          investmentName: x?.investmentName,
        };
      })
    )
  );

  let listOfInvestmentNames = {};

  const groupByInvestmentId = listOfSourceEmployeeInvestmentMap.reduce(
    (group, investment) => {
      const { investmentId, investmentName } = investment;
      listOfInvestmentNames[investmentId] = investmentName;
      group[investmentId] = group[investmentId] ?? [];
      group[investmentId].push(investment);
      return group;
    },
    {}
  );
  const InvestmentBalanceAggregatedDataForEachInvestment = Object.keys(
    listOfInvestmentNames
  ).map((investmentId, index) => {
    const totalBalanceOfInvestment = groupByInvestmentId[investmentId]?.reduce(
      (a, b) => a + b.accountBalance,
      0
    );
    return {
      investmentName: listOfInvestmentNames[investmentId],
      investmentId: investmentId,
      totalAvailableBalance: totalBalanceOfInvestment,
    };
  });

  return InvestmentBalanceAggregatedDataForEachInvestment;
}

////filter out sources for traditional and roth IRA
export const getDate = (date) => new Date(date);
export function FilterOutSourcesByDistributionMode(
  sourceBalances_,
  transaction_,
  withdrawal_
) {
  const sourceBalances = cloneDeep(sourceBalances_);
  const transaction = cloneDeep(transaction_);
  const withdrawal = cloneDeep(withdrawal_);
  const distributionMode = transaction[fields.distributionMode];
  const withdrawalType = withdrawal[fields.withdrawalType];
  let pretaxSources = [
    SourceSubCategory.EmployeeCatchUp,
    SourceSubCategory.EmployeePreTax,
    SourceSubCategory.EmployeePreTaxRollover,
    SourceSubCategory.EmployerQMAC,
    SourceSubCategory.EmployerQNEC,
  ];
  let filteredSourceBalances = sourceBalances;

  if (
    distributionMode === null ||
    distributionMode === undefined ||
    withdrawalType === null ||
    withdrawalType === undefined ||
    withdrawal[fields.modeOfPayment] === null ||
    withdrawal[fields.modeOfPayment] === undefined
  )
    return [];
  else if (distributionMode === DISTRIBUTION_WITHDRAWAL_MODE.traditional_IRA) {
    filteredSourceBalances = sourceBalances.filter((_) =>
      pretaxSources.includes(
        _.sourceSubCategory ?? SourceSubCategory.EmployeePreTax
      )
    );
  } else if (distributionMode === DISTRIBUTION_WITHDRAWAL_MODE.roth_IRA)
    filteredSourceBalances = sourceBalances.filter(
      (_) =>
        !pretaxSources.includes(
          _.sourceSubCategory ?? SourceSubCategory.EmployeePreTax
        )
    );

  return filteredSourceBalances;
}

export const getFilteredRkData = (
  rkData,
  isGross,
  withdrawal,
  transaction,
  maximumAmount
) => {
  const date = new Date();
  let today = new Date(date.setDate(date.getDate()));

  let filteredRkData = FilterOutSourcesByDistributionMode(
    rkData,
    transaction,
    withdrawal
  );

  let updatedFilteredRkData = CalculateUpdatedBalances(
    filteredRkData,
    withdrawal?.withDrawalType
  );

  let totalAccountBalance = updatedFilteredRkData?.reduce(
    (x, y) =>
      x +
      y.sourceEmployeeMap.regularAccountBalance +
      y.sourceEmployeeMap.basisAccountBalance +
      y.sourceEmployeeMap.repaymentAccountBalance,
    0
  );

  const splitMapping = (source_) => {
    let sourceEmployeeMap = source_.sourceEmployeeMap;
    let accountBalance =
      sourceEmployeeMap.regularAccountBalance +
      sourceEmployeeMap.basisAccountBalance +
      sourceEmployeeMap.repaymentAccountBalance;
    let sourceFraction = totalAccountBalance
      ? accountBalance / totalAccountBalance
      : 0;

    const investmentMappingFunction = (investment) => {
      let investmentBalance =
        investment.sourceEmployeeInvestmentMap.basisAccountBalance +
        investment.sourceEmployeeInvestmentMap.regularAccountBalance +
        investment.sourceEmployeeInvestmentMap.repaymentAccountBalance;

      let vestedInvestmentBalance =
        investment.sourceEmployeeInvestmentMap.basisAccountBalance +
        investment.sourceEmployeeInvestmentMap.regularAccountBalance +
        investment.sourceEmployeeInvestmentMap.repaymentAccountBalance;

      let investmentFraction = accountBalance
        ? investmentBalance / accountBalance
        : 0;

      return {
        investmentId: investment.investmentId,
        investmentName: investment.investmentName,
        sourceEmployeeInvestmentMap: {
          ...investment.sourceEmployeeInvestmentMap,
          investmentId: investment.investmentId,
          investmentName: investment.investmentName,
          investmentAccountBalance: investmentBalance,
          investmentFraction: investmentFraction,
          grossWithdrawalAmount: isGross
            ? totalAccountBalance < maximumAmount
              ? vestedInvestmentBalance
              : investmentFraction * maximumAmount * sourceFraction
            : 0,
          requestedAmount: isGross
            ? totalAccountBalance < maximumAmount
              ? vestedInvestmentBalance
              : investmentFraction * maximumAmount * sourceFraction
            : 0,
          accountBalance: investmentBalance,
        },
      };
    };

    return {
      ...source_,
      sourceInvestmentBalances: sourceEmployeeMap.sourceEmployeeInvestmentMaps.map(
        investmentMappingFunction
      ),
      sourceEmployeeMap: {
        ...sourceEmployeeMap,
        accountBalance: accountBalance,
        sourceFraction: sourceFraction,
        taxableEarningsFraction: totalAccountBalance
          ? (sourceEmployeeMap.regularEarningsBalance +
              sourceEmployeeMap.repaymentEarningsBalance +
              sourceEmployeeMap.basisEarningsBalance) /
            accountBalance
          : 0,
        grossWithdrawalAmount: isGross
          ? totalAccountBalance < maximumAmount
            ? accountBalance
            : maximumAmount * sourceFraction
          : 0,
        requestedAmount: isGross
          ? totalAccountBalance < maximumAmount
            ? accountBalance
            : maximumAmount * sourceFraction
          : 0,
        sourceEmployeeInvestmentMaps: sourceEmployeeMap.sourceEmployeeInvestmentMaps?.map(
          investmentMappingFunction
        ),
      },
    };
  };

  let x = updatedFilteredRkData?.map(splitMapping);
  return x;
};

///mapping fee and taxes to the data structure that is required
export const GetFeesAndTaxes = (values) => {
  let feesAndTaxes = [];
  if (values[fields.paymentMethod] === PaymentOptions.MailCheckToAddress)
    feesAndTaxes.push({
      WithdrawalAmountType: WithdrawalAmountType.ChequeFee,
      Amount: values[fields.checkFee],
    });
  else if (values[fields.paymentMethod] === PaymentOptions.EFT)
    feesAndTaxes.push({
      WithdrawalAmountType: WithdrawalAmountType.EFTFee,
      Amount: values[fields.eftFee],
    });
  if (true) {
    feesAndTaxes.push({
      WithdrawalAmountType: WithdrawalAmountType.StateTax,
      Amount: values[fields.stateTax],
    });
    feesAndTaxes.push({
      WithdrawalAmountType: WithdrawalAmountType.FederalTax,
      Amount: values[fields.federalTax],
    });
    feesAndTaxes.push({
      WithdrawalAmountType: WithdrawalAmountType.Penalty,
      Amount: values[fields.earlyWithdrawalPenalty],
    });
  }
};
export const getValues = (val) => {
  if ([null, undefined, ""].includes(val)) return 0;
  else if (isNaN(val)) return 0;
  else return val;
};
////
export const GetOverAllWithdrawalStrategy = (
  withdrawalRequestData,
  withdrawalTransaction
) => {
  switch (withdrawalTransaction?.sourceAllocationCalculationMethod) {
    case SourceAllocationCalculationMethod.Prorata:
      return WithdrawalStrategy.SourceProrationInvestmentProration;
    case SourceAllocationCalculationMethod.AllowMeToChoose:
    case SourceAllocationCalculationMethod.Hierarchy:
      if (
        get(withdrawalTransaction, fields.hierarchyCalculationMethod, [])
          .length === 2
      )
        return WithdrawalStrategy.SourceHierarchyInvestmentHierarchy;
      else if (
        !isOneTimePayment(withdrawalRequestData) &&
        get(
          withdrawalTransaction,
          fields.hierarchyCalculationMethod,
          []
        ).includes(1)
      )
        return WithdrawalStrategy.SourceHierarchyInvestmentProration;
      else if (
        !isOneTimePayment(withdrawalRequestData) &&
        get(
          withdrawalTransaction,
          fields.hierarchyCalculationMethod,
          []
        ).includes(2)
      )
        return WithdrawalStrategy.SourceProrationInvestmentHierarchy;
      else if (isOneTimePayment(withdrawalRequestData))
        return WithdrawalStrategy.AllowMeToChoose;
    default:
      return 0;
  }
};

export const feeAndTaxesModel = (withdrawalTransaction) => {
  return [
    {
      withdrawalAmountType: WithdrawalAmountType.NetRequestedAmount,
      amount: getValues(withdrawalTransaction[fields.requestedAmount]),
    },
    {
      withdrawalAmountType: WithdrawalAmountType.ChequeFee,
      amount: getValues(withdrawalTransaction[fields.checkFee]),
    },
    {
      withdrawalAmountType: WithdrawalAmountType.EFTFee,
      amount: getValues(withdrawalTransaction[fields.eftFee]),
    },
    // {
    //   withdrawalAmountType:WithdrawalAmountType.WithdrawalFee,
    //   amount :  getValues(withdrawalTransaction[fields.withdrawalFee])
    // },
    {
      withdrawalAmountType: WithdrawalAmountType.RollOverFee,
      amount: getValues(withdrawalTransaction[fields.rollOverFee]),
    },
    {
      withdrawalAmountType: WithdrawalAmountType.StateTax,
      amount: getValues(withdrawalTransaction[fields.stateTax]),
    },
    {
      withdrawalAmountType: WithdrawalAmountType.FederalTax,
      amount: getValues(withdrawalTransaction[fields.federalTax]),
    },
    {
      withdrawalAmountType: WithdrawalAmountType.Penalty,
      amount: !withdrawalTransaction?.waiveOffLimitApplicable
        ? getValues(withdrawalTransaction[fields.earlyWithdrawalPenalty])
        : getValues(withdrawalTransaction[fields.waiveOffLimit]),
    },
  ];
};
export const getSourceWiseRequestAmountModel = (transaction) =>
  (get(transaction, "allocatedSourceWithdrawal") ?? []).map((_) => {
    return {
      sourceId: _.sourceId,
      amountRequested: getValues(
        _?.sourceEmployeeMap?.sourceWithdrawalAmount ?? _.sourceWithdrawalAmount
      ),
    };
  });
export const getSourceWiseRequestAmountModelInitial = (transaction) =>
  (get(transaction, "allocatedSourceWithdrawal") ?? []).map((_) => {
    return {
      sourceId: _.sourceId,
      amountRequested: getValues(
        _?.sourceWithdrawalAmount ??
          _?.sourceEmployeeMap?.sourceWithdrawalAmount
      ),
    };
  });

export const calculateEffectiveSourceWithdrawal = (
  transaction,
  withdrawalRequestData,
  tempFilteredSourceBalances
) => {
  const filteredSourceBalances = cloneDeep(tempFilteredSourceBalances);
  let sourceWiseRequestAmount = getSourceWiseRequestAmountModelInitial(
    transaction
  );
  const feeAndTaxes = feeAndTaxesModel(transaction);
  let grossAmountSplit = [];

  if (filteredSourceBalances.length > 0)
    grossAmountSplit = CalculateGrossAmountSplit(
      filteredSourceBalances,
      withdrawalRequestData?.withdrawalType,
      feeAndTaxes,
      GetOverAllWithdrawalStrategy(withdrawalRequestData, transaction),
      transaction[fields.sourceHierarchyDetails],
      transaction[fields.requestedAmount],
      sourceWiseRequestAmount,
      withdrawalRequestData
    );
  return grossAmountSplit;
};
export const calculateGrossAmountFromNetAmountSourceWise = (
  transaction,
  withdrawalRequestData,
  tempFilteredSourceBalances
) => {
  const grossAmount = calculateEffectiveSourceWithdrawal(
    transaction,
    withdrawalRequestData,
    tempFilteredSourceBalances
  );
  const effectiveGrossAmount = grossAmount.reduce(
    (a, v) => (a = a + getValues(v.sourceEmployeeMap?.accountBalance)),
    0
  );
  return effectiveGrossAmount;
};

export const calculateNetAmountFromGrossAmount = (
  transaction,
  withdrawalRequestData,
  tempFilteredSourceBalances
) => {
  let sourceWiseRequestAmount = getSourceWiseRequestAmountModel(transaction);
  const filteredSourceBalances = cloneDeep(tempFilteredSourceBalances);
  const feeAndTaxes = feeAndTaxesModel(transaction);
  const totalFees = CalculateTotalFees(feeAndTaxes);
  const withdrawalStrategy = GetOverAllWithdrawalStrategy(
    withdrawalRequestData,
    transaction
  );

  let feesProration = [];

  if (
    [
      WithdrawalStrategy.SourceHierarchyInvestmentProration,
      WithdrawalStrategy.SourceHierarchyInvestmentHierarchy,
      WithdrawalStrategy.SourceProrationInvestmentHierarchy,
    ].includes(withdrawalStrategy)
  ) {
    feesProration = GetWithdrawalAmountBasedOnStrategy(
      2,
      filteredSourceBalances,
      transaction[fields.sourceHierarchyDetails],
      totalFees,
      []
    );

    const balanceExcludingFees = DeductProratedSplits(
      filteredSourceBalances,
      feesProration,
      1
    );
    const netWithdrawalAmount = GetWithdrawalAmountBasedOnStrategy(
      withdrawalStrategy,
      balanceExcludingFees,
      transaction[fields.sourceHierarchyDetails],
      transaction[fields.grossAmount] - totalFees,
      sourceWiseRequestAmount
    );
    const balanceExcludingFeesAndTaxes = CalculateTaxSplitUpForNetAmount(
      netWithdrawalAmount,
      feeAndTaxes
    );
    return balanceExcludingFeesAndTaxes;
  } else
    feesProration = CalculateProrationSplitUp(
      filteredSourceBalances,
      totalFees,
      sourceWiseRequestAmount,
      false
    );

  const netWithdrawalAmount = GetWithdrawalAmountBasedOnStrategy(
    withdrawalStrategy,
    filteredSourceBalances,
    transaction[fields.sourceHierarchyDetails],
    transaction[fields.grossAmount],
    sourceWiseRequestAmount
  );
  const balanceExcludingFees = DeductProratedSplits(
    netWithdrawalAmount,
    feesProration,
    1
  );
  const balanceExcludingFeesAndTaxes = CalculateTaxSplitUpForNetAmount(
    balanceExcludingFees,
    feeAndTaxes
  );
  return balanceExcludingFeesAndTaxes;
};

export const getTaxesAndFeeForWithdrawalTransaction = (
  transaction,
  withdrawalRequestData,
  sourceBalances
) => {
  let filteredSourceBalances = getFilteredRkData(
    sourceBalances,
    false,
    withdrawalRequestData,
    transaction,
    0
  );
  return (
    calculateGrossAmountFromNetAmountSourceWise(
      transaction,
      withdrawalRequestData,
      filteredSourceBalances
    ) - transaction[fields.requestedAmount]
  );
};

export const calculateNextPaymentDate = (withdrawalPlanSetUp) => {
  const frequencyType = withdrawalPlanSetUp?.paymentFrequencyApplicable;
  const dateInput = withdrawalPlanSetUp?.startDay;
  let monthInput = null;

  switch (frequencyType) {
    case 4:
      let date = moment(
        `${moment().format("MM")}//${dateInput}//${moment().format("YYYY")}`
      );
      date =
        parseInt(moment().format("MM")) >= parseInt(dateInput)
          ? date.add(1, "M")
          : date;
      return date.format("MM/DD/YYYY");

    case 5:
      monthInput = withdrawalPlanSetUp?.startMonthFirstQuarter;
      date = moment(`${monthInput}//${1}//${moment().format("YYYY")}`);
      let dates = [];
      dates.push(date);
      for (let i = 0; i < 4; i++) {
        dates.push(dates[dates.length - 1].add(3, "M"));
      }
      return dates.find((_) => _ > moment())?.format("MM/DD/YYYY");

    case 6:
      monthInput = withdrawalPlanSetUp?.startMonthFirstHalfYear;
      date = moment(`${monthInput}//${1}//${moment().format("YYYY")}`);
      dates = [];
      dates.push(date);
      for (let i = 0; i < 2; i++) {
        dates.push(dates[dates.length - 1].add(6, "M"));
      }
      return dates.find((_) => _ > moment())?.format("MM/DD/YYYY");

    case 7:
      monthInput = withdrawalPlanSetUp?.startMonth;
      date = moment(`${monthInput}//${dateInput}//${moment().format("YYYY")}`);
      dates = [];
      dates.push(...[date, date.add(1, "years")]);
      return dates.find((_) => _ > moment())?.format("MM/DD/YYYY");
  }
};

export const GetWithdrawalStrategy = (withdrawalTransaction) => {
  switch (withdrawalTransaction?.sourceAllocationCalculationMethod) {
    case SourceAllocationCalculationMethod.Prorata:
      return WithdrawalStrategy.SourceProrationInvestmentProration;
    case SourceAllocationCalculationMethod.AllowMeToChoose:
      return WithdrawalStrategy.AllowMeToChoose;
    case SourceAllocationCalculationMethod.Hierarchy:
      if (
        get(withdrawalTransaction, fields.hierarchyCalculationMethod, [])
          .length === 2
      )
        return WithdrawalStrategy.SourceHierarchyInvestmentHierarchy;
      else if (
        get(
          withdrawalTransaction,
          fields.hierarchyCalculationMethod,
          []
        ).includes(1)
      )
        return WithdrawalStrategy.SourceHierarchyInvestmentProration;
      else if (
        get(
          withdrawalTransaction,
          fields.hierarchyCalculationMethod,
          []
        ).includes(2)
      )
        return WithdrawalStrategy.SourceProrationInvestmentHierarchy;
    default:
      return 0;
  }
};

export const calculateRequestedAmountEffective = (
  withdrawalRequestData,
  withdrawalTransaction,
  filteredSourceBalances
) => {
  if (isAllowMeToChooseTransaction(withdrawalTransaction)) {
    return get(
      withdrawalTransaction,
      fields.allocatedSourceWithdrawal,
      []
    ).reduce(
      (a, b) =>
        a +
        getValues(
          b?.sourceEmployeeMap?.sourceWithdrawalAmount ??
            b?.sourceWithdrawalAmount
        ),
      0
    );
  }
  const requestedAmount = !calculationToBeBasedOnGrossAmount(
    withdrawalRequestData
  )
    ? withdrawalTransaction[fields.requestedAmount]
    : calculateNetAmountFromGrossAmount(
        withdrawalTransaction,
        withdrawalRequestData,
        filteredSourceBalances
      ).reduce(
        (a, b) => a + getValues(b?.sourceEmployeeMap?.accountBalance),
        0
      );
  console.log("1234requestedAmount", requestedAmount);
  return requestedAmount;
};
